import { Injectable } from '@angular/core';
import {
  AlignmentType,
  BorderStyle,
  convertInchesToTwip,
  Document,
  HeightRule,
  IStylesOptions,
  ImageRun,
  LevelFormat,
  LineRuleType,
  Packer,
  PageBreak,
  PageOrientation,
  Paragraph,
  ShadingType,
  Table,
  Tab,
  TabStopType,
  TableCell,
  TableLayoutType,
  TableRow,
  TextRun,
  VerticalAlign,
  WidthType,
} from 'docx';
import { saveAs } from 'file-saver';

import {
  CreditSectionBlock,
  DisbursementRepaymentSummaryReportData,
  DisbursementRepaymentSummaryReportRow,
  FinancialAnalysisReportData,
  FinancialAnalysisReportRow,
  NestedReportBlock,
  NestedTable,
  NestedTableColumn,
  RepaymentPlanReportData,
  RepaymentPlanReportRow,
  ReportSection,
  SectionJsonData,
} from '../models/Credit report.model';
import { SectionType } from '../models/section-type.enum';
import { PKL_GRP } from '../../../../core/shared/models/picklist.model';
import { isRichTextHtml, joinRichText, richTextToPlainText, sanitizeRichText } from '../utils/rich-text.util';
import { formatTableCell, tableLeafTitles } from '../utils/table-cell-format.util';
import { scopedListLabel } from '../utils/scoped-numbering.util';
import { flattenRepaymentTrancheRows } from '../utils/repayment-plan-rows.util';
import { splitRepaymentPlanByType } from '../utils/repayment-plan-by-type.util';

const NUMBERING_REFERENCE = 'credit-report-numbering';

/**
 * Editor body text size (px). Rich-text font sizes are exported relative to
 * it, so they scale with the Word paragraph style they land in.
 */
const RICH_TEXT_BASE_PX = 15;

/** Inline formatting accumulated while walking rich-text HTML. */
interface RichTextMarks {
  bold?: boolean;
  italics?: boolean;
  underline?: boolean;
  strike?: boolean;
  color?: string;
  highlight?: string;
  font?: string;
  sizePx?: number;
}

/** CSS colour (`#abc`, `#aabbcc`, `rgb(...)`) → Word hex (`AABBCC`); '' if unusable. */
function cssColorToHex(value: string | null | undefined): string {
  const color = String(value ?? '').trim();

  const hex = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const digits = hex[1].length === 3 ? hex[1].replace(/./g, (d) => d + d) : hex[1];
    return digits.toUpperCase();
  }

  const rgb = color.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb) {
    return rgb
      .slice(1, 4)
      .map((channel) => Math.min(255, Number(channel)).toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();
  }

  return '';
}

/** One Word document `sections[]` entry — portrait (default) or the dedicated Repayment Plan landscape page. */
interface DocumentSectionEntry {
  properties: {
    page: {
      size: {
        width: number;
        height: number;
        orientation: (typeof PageOrientation)[keyof typeof PageOrientation];
      };
      margin: {
        top: number;
        bottom: number;
        left: number;
        right: number;
        header: number;
        footer: number;
      };
    };
  };
  children: Array<Paragraph | Table>;
}

/* ====================================================== */
/* REFERENCE DOCUMENT CONFIGURATION                        */
/* ====================================================== */

const BLACK = '000000';
const WHITE = 'FFFFFF';
const FONT = 'Arial';

/*
 * Centralized typography. Body text must read naturally across multiple
 * pages -- never shrunk to force content onto one page. Only the
 * Highlights sidebar and table cells are intentionally smaller than
 * normal report body text.
 *
 * lineRule is always AUTO (a multiple of single line height, "line/240"),
 * never EXACT/AT_LEAST -- those clamp row height and are what causes text
 * to visually clip/compress. contextualSpacing is always explicitly
 * false so consecutive paragraphs never silently collapse their spacing.
 */
const BODY_TEXT_SIZE = 22; // 11pt
const BODY_LINE_SPACING = 312; // 1.30
const BODY_SPACING_AFTER = 160; // 8pt

/**
 * The first-page Executive Summary column is narrower than a full report
 * page, so it gets its own LEFT-aligned body style -- justifying text in
 * a narrow column produces irregular word gaps.
 */
const EXEC_BODY_TEXT_SIZE = 21; // 10.5pt
const EXEC_BODY_LINE_SPACING = 300; // 1.25
const EXEC_BODY_SPACING_AFTER = 140; // 7pt

const MAIN_HEADING_SIZE = 24; // 12pt
const MAIN_HEADING_BEFORE = 240; // 12pt
const MAIN_HEADING_AFTER = 120; // 6pt
const MAIN_HEADING_LINE = 252; // ~1.05

const SUB_HEADING_SIZE = 22; // 11pt
const SUB_HEADING_BEFORE = 200; // 10pt
const SUB_HEADING_AFTER = 100; // 5pt

const SMALL_HEADING_SIZE = 20; // 10pt (table/caption headings)
const SMALL_HEADING_BEFORE = 80;
const SMALL_HEADING_AFTER = 40;

const EXEC_SUBHEADING_SIZE = 21; // 10.5pt, a)/b)/c)
const EXEC_SUBHEADING_BEFORE = 120; // 6pt
const EXEC_SUBHEADING_AFTER = 60; // 3pt

const INSTITUTION_NAME_SIZE = 24; // 12pt
const EXEC_SUMMARY_TITLE_SIZE = 22; // 11pt

const SIDEBAR_TEXT_SIZE = 19; // 9.5pt
const SIDEBAR_LINE_SPACING = 264; // ~1.10
const SIDEBAR_SPACING_AFTER = 80; // one full highlight record per paragraph
const SIDEBAR_INDENT_LEFT = 180;
const SIDEBAR_INDENT_HANGING = 100;

const SIDEBAR_HEADING_SIZE = 20; // 10pt, "Highlights"
const SIDEBAR_HEADING_LINE = 240; // single (1.0)
const SIDEBAR_HEADING_AFTER = 100; // 5pt

/** Reserved for dense table-cell paragraphs only (recommendation cells, captions) -- never main report body text. */
const TABLE_TEXT_SIZE = 19; // 9.5pt
const TABLE_SPACING_AFTER = 30;
const TABLE_LINE_SPACING = 264;

/** Table header cells: same line/after rhythm, one step larger and bold. */
const TABLE_HEADER_TEXT_SIZE = 20; // 10pt

/** MRC / BRC signature page under Summary Terms and Conditions — small, as in the reference. */
const SIGNATURE_TEXT_SIZE = 16; // 8pt

/* ---------- MRC / BRC signature page ---------- */
/* Printed under "Summary Terms and Conditions" — the reference document's */
/* recommendation blocks. Exported so the on-screen preview shows the same. */

export interface RecommendationSignatory {
  name: string;
  /** One entry per printed line, as broken in the reference. */
  titleLines: string[];
}

export interface RecommendationSignatureBlock {
  /** Bullet line above the block. Null for a block without one. */
  caption: string | null;
  signatories: RecommendationSignatory[];
}

/** refValSeq of the Summary Terms and Conditions section. */
export const SUMMARY_TERMS_REF_VAL_SEQ = 2682;

export const RECOMMENDATION_SIGNATURE_BLOCKS: RecommendationSignatureBlock[] = [
  {
    caption: 'Recommended for consideration of MRC.',
    signatories: [
      { name: 'Tufail Ahmed', titleLines: ['Assistant Vice President'] },
      { name: 'Ali Said', titleLines: ['Vice President'] },
      {
        name: 'Saqib Siddiqui',
        titleLines: [
          'Head of Sector Development &',
          'A / Head of Portfolio',
          'Management Department',
        ],
      },
    ],
  },
  {
    caption: 'Recommended for consideration and approval of BRC.',
    signatories: [
      {
        name: 'Saqib Siddiqui',
        titleLines: [
          'Head of Sector Development &',
          'A/ Head of Portfolio',
          'Management Department',
        ],
      },
      { name: 'Rashid Imran', titleLines: ['Chief Risk &', 'Compliance Officer'] },
      { name: 'Hassan Nawaz', titleLines: ['A/Chief Financial', 'Officer'] },
      {
        name: 'Bilal Abdul Razzaq',
        titleLines: ['Lead Corporate Finance', '& Investment Banking'],
      },
    ],
  },
  {
    caption: null,
    signatories: [
      { name: 'Yasir Ashfaq', titleLines: ['Chief Executive Officer'] },
    ],
  },
];

/** Institutional Highlights table: compact like the reference, so full-PKR amounts fit on one line. */
const FIN_HIGHLIGHTS_TEXT_SIZE = 15; // 7.5pt
const TABLE_HEADER_LINE_SPACING = 252;

/**
 * The Repayment Plan table (2646) can have well over a dozen period
 * columns, so it uses a smaller size than other report tables to keep
 * every header label ("Tranche Amount", period months) on one line
 * without needing wider columns than the landscape page has room for.
 */
const REPAYMENT_TABLE_TEXT_SIZE = 16; // 8pt
const REPAYMENT_TABLE_HEADER_TEXT_SIZE = 17; // 8.5pt

const NONE_BORDER = {
  style: BorderStyle.NONE,
  size: 0,
  color: WHITE,
};

const THIN_BORDER = {
  style: BorderStyle.SINGLE,
  size: 4,
  color: BLACK,
};

const MEDIUM_BORDER = {
  style: BorderStyle.SINGLE,
  size: 6,
  color: BLACK,
};

/*
 * US Letter page. Balanced portrait margins: 0.75in top/bottom (1080
 * twips), 0.80in left/right (1152 twips).
 */
const PAGE_WIDTH_TWIPS = 12240;
const PAGE_HEIGHT_TWIPS = 15840;
const DEFAULT_PAGE_MARGIN_TOP_BOTTOM_INCHES = 0.75;
const DEFAULT_PAGE_MARGIN_LEFT_RIGHT_INCHES = 0.8;

/* ====================================================== */
/* COVER PAGE (page 1 of the reference Credit Proposal)    */
/* ====================================================== */

/** PMIC logo copied from the reference document into `public/images`. */
const COVER_LOGO_PATH = 'images/pmic-logo.png';
/** Logo display size in the reference document (2343150 x 756333 EMU). */
const COVER_LOGO_WIDTH_PX = 246;
const COVER_LOGO_HEIGHT_PX = 79;
const COVER_FONT = 'Calibri';
/** Reference cover page is A4 with 1in margins. */
const COVER_PAGE_WIDTH_TWIPS = 11906;
const COVER_PAGE_HEIGHT_TWIPS = 16838;
const COVER_PAGE_MARGIN_TWIPS = 1440;

/** Text printed on the cover page. Defaults mirror the reference document exactly. */
export interface CreditProposalCoverPageOptions {
  title?: string;
  programmeName?: string;
  facilities?: string[];
  dateLabel?: string;
  companyName?: string;
}

const DEFAULT_COVER_PAGE: Required<CreditProposalCoverPageOptions> = {
  title: 'Credit Proposal ',
  programmeName: 'Damen Support Programme (DSP)',
  facilities: ['Term Finance ', 'Credit Ensure Facility', 'Islamic Finance'],
  dateLabel: 'October 2024',
  companyName: 'Pakistan Microfinance Investment Company',
};

/** Heading above the consolidated (Type column) Tentative Repayment Plan table. */
const CONSOLIDATED_REPAYMENT_PLAN_TITLE = 'Consolidated Tentative Repayment Plan (PKR)';

/** Highlights sidebar / gutter / Executive Summary column split, as a percentage of usable page width. */
const DEFAULT_HIGHLIGHTS_COLUMN_PERCENT = 19;
const GUTTER_COLUMN_PERCENT = 1.5;

/**
 * Keys of the six on-screen report tables that are embedded as image
 * snapshots (html2canvas) instead of being rebuilt as native docx tables.
 */
export type CapturedTableKey =
  | 'financial-analysis-details'
  | 'repayment-plan'
  | 'disbursement-summary'
  | 'institutional-rating'
  | 'recommendation'
  | 'financial-analysis-summary';

/** A single on-screen table captured to a PNG data URL by the caller. */
export interface CapturedTableImage {
  /** PNG data URL produced by html2canvas (`canvas.toDataURL('image/png')`). */
  dataUrl: string;
  /** Natural pixel width of the capture canvas. */
  width: number;
  /** Natural pixel height of the capture canvas. */
  height: number;
  /** CSS layout width of the source element, in px (used to avoid upscaling). */
  cssWidth?: number;
  /** CSS layout height of the source element, in px. */
  cssHeight?: number;
  /**
   * Height of the table header (`<thead>`) from the top of the capture, in
   * canvas px. When set, the header is repeated at the top of every
   * continuation page of a table that spans several pages.
   */
  headerHeight?: number;
  /**
   * Bottom edge of every body row, in canvas px from the top of the capture.
   * When set, page slices end on a row boundary so no row is cut in half.
   */
  rowBreaks?: number[];
}

/**
 * Captured tables per key. A key may hold several tables (e.g. the Repayment
 * Plan has one table per repayment term type); each one starts on a new page.
 */
export type CapturedTableImages = Partial<
  Record<CapturedTableKey, CapturedTableImage | CapturedTableImage[]>
>;

/**
 * One row of the "Profile of Board of Directors" table (Annexure-III (a)),
 * rendered as a native docx table (not a captured image).
 */
export interface BoardDirectorReportRow {
  /** flNm */
  name: string;
  /** pstn — shown under the name in parentheses */
  position: string;
  /** qlfctn */
  qualification: string;
  /** Committee name(s) the director belongs to (matched via committees API) */
  committee: string;
  /** arOfExprtse */
  experience: string;
}

/**
 * One row of the "List of Key Management" table (Annexure-III (b)),
 * rendered as a native docx table (not a captured image).
 */
export interface KeyManagementReportRow {
  /** pstn */
  position: string;
  /** flNm */
  name: string;
  /** qlfctn */
  qualification: string;
  /** arOfExprtse */
  experience: string;
}

/**
 * One committee of the "Committee Details" table (Annexure-III (c)),
 * rendered as a native docx table (not a captured image).
 */
export interface CommitteeReportRow {
  /** cmtNm */
  committee: string;
  members: CommitteeMemberReportRow[];
}

export interface CommitteeMemberReportRow {
  /** flNm */
  name: string;
  /** dsgntn / pstn label */
  position: string;
  /** qlfctn label */
  qualification: string;
}

/**
 * Organogram image uploaded on the Credit Proposal's Documents tab, shown
 * as Annexure-III (d).
 */
export interface OrganogramReportImage {
  /** Image as a data URL (`data:image/png;base64,...`). */
  dataUrl: string;
  type: 'png' | 'jpg';
}

/**
 * One document uploaded on the Credit Proposal's Attachments tab, listed in
 * the report's "Attachments" section below the Organogram.
 */
export interface AttachmentReportItem {
  /** Document type label, e.g. "Projected Cash Flows of Borrower". */
  title: string;
  /** Uploaded file name. */
  fileName: string;
  /** The file itself when it is a PNG/JPEG image; other files are only named. */
  image: OrganogramReportImage | null;
}

export interface CreditReportExportOptions {
  /**
   * Cover page (first page of the reference document: PMIC logo, title,
   * programme, facilities, date and company name inside a single border).
   * Pass `false` to omit it; any field left out uses the reference text.
   */
  coverPage?: CreditProposalCoverPageOptions | false;

  /** Institution heading displayed above Executive Summary. */
  institutionName?: string;

  /**
   * On-screen snapshots of the six specially-styled report tables. When a
   * key is present, that table is embedded as an image (sliced across pages
   * when taller than one page) in place of its native docx table. Missing
   * keys fall back to the native docx table rendering unchanged.
   */
  tableImages?: CapturedTableImages;

  /**
   * Rows for the "Profile of Board of Directors" table (Annexure-III (a)),
   * rendered as a native docx table immediately before the Financial
   * Analysis section. Sourced by the caller from the onboarding APIs.
   */
  boardOfDirectors?: BoardDirectorReportRow[];

  /**
   * Rows for the "List of Key Management" table (Annexure-III (b)), rendered
   * as a native docx table immediately after the Board of Directors table.
   */
  keyManagement?: KeyManagementReportRow[];

  /**
   * Committees and their members for the "Committee Details" table
   * (Annexure-III (c)), placed right after the List of Key Management.
   */
  committees?: CommitteeReportRow[];

  /** Organogram image for Annexure-III (d), placed after Committee Details. */
  organogram?: OrganogramReportImage | null;

  /** Attachments tab documents, listed after the Organogram. */
  attachments?: AttachmentReportItem[];

  /**
   * Width of the first-page Highlights sidebar as a percentage of the
   * usable (post-margin) page width. Default: 19. A fixed 1.5% gutter
   * column and the remaining width (Executive Summary column, ~79.5%) are
   * derived from this automatically.
   */
  highlightsColumnPercent?: number;

  /** Main title displayed on the first page. */
  executiveSummaryTitle?: string;

  /** Small title displayed above the left-hand highlights column. */
  highlightsTitle?: string;

  /** Picklist refValSeq containing Highlights. Default: 2533. */
  highlightsRefValSeq?: number;

  /**
   * Sections placed in the right side of the first-page Executive Summary.
   * Defaults match the supplied reference document.
   */
  executiveSummaryRefValSeqs?: number[];

  /** Do not add the reference first-page summary layout. */
  disableExecutiveSummaryLayout?: boolean;

  /**
   * Page margin, in inches. If provided, applies to all four sides
   * uniformly (back-compat override). Default when omitted: 0.75in
   * top/bottom, 0.80in left/right.
   */
  pageMarginInches?: number;

  /**
   * Opening exposure, shown by the Repayment Plan (2646) and Disbursement &
   * Repayment Summary (2645) tables' Opening Exposure row. The export
   * service has no API access of its own, so the caller must supply
   * whatever it already fetched for the on-screen report.
   */
  openingExposure?: number;

  /**
   * Repayment Plan (2646) data exactly as the on-screen report built it —
   * sourced from the live saved plan (not the first-save block snapshot) and
   * with repayment-term labels resolved. Used for the native docx fallback
   * table; when omitted the section block is read instead.
   */
  repaymentPlanData?: RepaymentPlanReportData | null;

  /**
   * Financial Analysis summary rows/columns — independent of `reportSections`
   * (sourced directly from GET /crdt-prpsl/get-fnc-anlsys by the caller).
   * Rendered as its own trailing section, immediately after Repayment Plan
   * (2646), the same way it's positioned on-screen in GenrateCreditReport.
   */
  financialAnalysisSummaryRows?: FinancialAnalysisReportRow[];
  financialAnalysisSummaryColumns?: string[];
}

@Injectable({ providedIn: 'root' })
export class CreditReportExportService {
  /**
   * Page geometry derived from options at the start of each export. Kept as
   * instance state (rather than module constants) so highlightsColumnPercent
   * and pageMarginInches are actually honoured per-call.
   */
  private pageMarginTopBottomTwips = convertInchesToTwip(
    DEFAULT_PAGE_MARGIN_TOP_BOTTOM_INCHES,
  );

  private pageMarginLeftRightTwips = convertInchesToTwip(
    DEFAULT_PAGE_MARGIN_LEFT_RIGHT_INCHES,
  );

  private pageContentWidthTwips = 0;
  private pageContentHeightTwips = 0;
  private highlightsColumnWidthTwips = 0;
  private gutterColumnWidthTwips = 0;
  private executiveColumnWidthTwips = 0;

  /**
   * True only while building paragraphs that live inside the first-page
   * Executive Summary column. That column is narrower than a full report
   * page, so its body text uses a dedicated smaller size/spacing (see
   * EXEC_BODY_TEXT_SIZE/EXEC_BODY_LINE_SPACING) than the full-width
   * main-body text, though both are justified the same way.
   */
  private executiveSummaryContext = false;

  /** One Word numbering instance per independent numbered-list block, so each restarts at 1. */
  private numberingReferences: string[] = [];

  /** refValSeq constants mapped explicitly, mirroring GenrateCreditReport. */
  private readonly FINANCIAL_ANALYSIS_DETAILS_REF_VAL_SEQ = 2647;
  private readonly DISBURSEMENT_REPAYMENT_SUMMARY_REF_VAL_SEQ = 2645;
  private readonly REPAYMENT_PLAN_REF_VAL_SEQ = 2646;

  /** 'introduction' section in CREDIT_PROPOSAL_TEMPLATE — Financial Analysis Details is pinned right after it. */
  private readonly INTRODUCTION_REF_VAL_SEQ = 2525;

  /** Sections numbered as a sub-heading of the one before them (2527 = Financial Performance → "2.1"). */
  private readonly SUB_SECTION_REF_VAL_SEQS = [2527];

  /** Score on Institutional Rating System — printed right after Introduction, as in the reference proposal. */
  private readonly SCORE_ON_IRS_REF_VAL_SEQ = 2526;

  /** Supplied via CreditReportExportOptions.openingExposure for the current export call. */
  private openingExposure = 0;
  private repaymentPlanData: RepaymentPlanReportData | null = null;

  /**
   * Pre-sliced, ready-to-insert image paragraphs per captured table key for
   * the current export call. Prepared once (async) before the document is
   * assembled, then spliced in synchronously in place of native tables.
   */
  private capturedTableImageParagraphs = new Map<
    CapturedTableKey,
    Paragraph[]
  >();

  private nextNumberingReference(): string {
    const reference = `${NUMBERING_REFERENCE}-${this.numberingReferences.length}`;
    this.numberingReferences.push(reference);
    return reference;
  }

  /* ====================================================== */
  /* PUBLIC ENTRY POINT                                    */
  /* ====================================================== */

  /**
   * Heading label of every section exactly as the Word report prints it, so
   * the on-screen preview can show the same "a)" … "g)" / "1." labels:
   * Executive Summary sections a), b)…, the sections before Introduction
   * carry on those letters, Introduction onwards are numbered. The Highlights
   * sidebar has no label. `financialAnalysis` is the label of the trailing
   * Financial Analysis section.
   */
  getSectionHeadingLabels(
    reportSections: ReportSection[],
    options: CreditReportExportOptions = {},
  ): { bySection: Map<number, string>; financialAnalysis: string } {
    const { executiveSections, canBuildReferenceFirstPage, remainingSections } =
      this.partitionReportSections(reportSections, options);
    const letterOffset = canBuildReferenceFirstPage ? executiveSections.length : 0;
    const bySection = new Map<number, string>();

    if (canBuildReferenceFirstPage) {
      executiveSections.forEach((section, index) => {
        bySection.set(Number(section.refValSeq), `${this.alphabeticPrefix(index)})`);
      });
    }

    remainingSections.forEach((section, index) => {
      bySection.set(
        Number(section.refValSeq),
        this.sectionHeadingLabel(remainingSections, index, letterOffset),
      );
    });

    return {
      bySection,
      financialAnalysis: this.sectionHeadingLabel(
        remainingSections,
        remainingSections.length,
        letterOffset,
      ),
    };
  }

  /**
   * Splits the report into the first page's Highlights sidebar and Executive
   * Summary sections, and the remaining sections in their final order
   * (Introduction → Score on Institutional Rating System → Financial
   * Analysis Details → the rest).
   */
  private partitionReportSections(
    reportSections: ReportSection[],
    options: CreditReportExportOptions,
  ): {
    highlightSection: ReportSection | undefined;
    executiveSections: ReportSection[];
    canBuildReferenceFirstPage: boolean;
    remainingSections: ReportSection[];
  } {
    const visibleSections = (reportSections ?? [])
      .filter((section) => Array.isArray(section.blocks) && section.blocks.length > 0)
      .sort(
        (first, second) =>
          Number(first.templateOrder ?? first.sectionOrder ?? Number.MAX_SAFE_INTEGER) -
            Number(second.templateOrder ?? second.sectionOrder ?? Number.MAX_SAFE_INTEGER) ||
          Number(first.refValSeq) - Number(second.refValSeq),
      );

    const highlightsRefValSeq = Number(options.highlightsRefValSeq ?? 2533);
    const executiveSummaryRefValSeqs =
      options.executiveSummaryRefValSeqs ?? [2519, 2647, 2521, 2522, 2523];

    const highlightSection = visibleSections.find(
      (section) => Number(section.refValSeq) === highlightsRefValSeq,
    );

    const executiveSections = executiveSummaryRefValSeqs
      .map((refValSeq) =>
        visibleSections.find(
          (section) => Number(section.refValSeq) === Number(refValSeq),
        ),
      )
      .filter(
        (section): section is ReportSection => section !== undefined,
      );

    const canBuildReferenceFirstPage =
      options.disableExecutiveSummaryLayout !== true &&
      Boolean(highlightSection || executiveSections.length > 0);

    const usedSectionReferences = new Set<number>(
      canBuildReferenceFirstPage
        ? [
            ...(highlightSection ? [Number(highlightSection.refValSeq)] : []),
            ...executiveSections.map((section) => Number(section.refValSeq)),
          ]
        : [],
    );

    const remainingSections = this.moveSectionAfterIntroduction(
      this.moveSectionAfterIntroduction(
        visibleSections.filter(
          (section) => !usedSectionReferences.has(Number(section.refValSeq)),
        ),
        this.FINANCIAL_ANALYSIS_DETAILS_REF_VAL_SEQ,
      ),
      this.SCORE_ON_IRS_REF_VAL_SEQ,
    );

    return {
      highlightSection,
      executiveSections,
      canBuildReferenceFirstPage,
      remainingSections,
    };
  }

  async exportToWord(
    reportSections: ReportSection[],
    fileName = 'Credit-Proposal-Report',
    options: CreditReportExportOptions = {},
  ): Promise<void> {
    this.computePageGeometry(options);
    this.numberingReferences = [];
    this.openingExposure = Number(options.openingExposure ?? 0);
    this.repaymentPlanData = options.repaymentPlanData ?? null;
    this.capturedTableImageParagraphs =
      await this.prepareCapturedTableImageParagraphs(options.tableImages);

    const children: Array<Paragraph | Table> = [];

    const {
      highlightSection,
      executiveSections,
      canBuildReferenceFirstPage,
      remainingSections,
    } = this.partitionReportSections(reportSections, options);

    let movedToFirstPageCount = 0;

    if (canBuildReferenceFirstPage) {
      // Sections that follow the summary, in their final order, so the
      // first page can pull the leading ones up beside the Highlights.
      const firstPage = this.buildExecutiveSummaryPage(
        highlightSection ?? null,
        executiveSections,
        options,
        remainingSections,
      );

      movedToFirstPageCount = firstPage.movedSectionCount;
      children.push(firstPage.table);
    }

    // The Repayment Plan table (2646) can have well over a dozen period
    // columns — it needs its own landscape page(s) to fit without the
    // narrow-column letter-wrapping a portrait page would force. Every
    // other section stays on the shared portrait section(s).
    const documentSections: DocumentSectionEntry[] = [];

    // Letters a), b)… of the first page's Executive Summary carry on into the
    // sections before Introduction (see sectionHeadingLabel).
    const headingLetterOffset = canBuildReferenceFirstPage
      ? executiveSections.length
      : 0;

    let portraitChildren = children;

    remainingSections.forEach((section, index) => {
      // Already placed beside the Highlights on the first page.
      if (index < movedToFirstPageCount) {
        return;
      }

      const sectionContent = this.buildSection(
        section,
        index + 1,
        this.sectionHeadingLabel(remainingSections, index, headingLetterOffset),
      );

      if (Number(section.refValSeq) === this.REPAYMENT_PLAN_REF_VAL_SEQ) {
        // Each `sections[]` entry always starts a fresh page — an empty one
        // pushed here (e.g. Repayment Plan being the very first remaining
        // section) would otherwise print as a blank page before the table.
        if (portraitChildren.length > 0) {
          documentSections.push({
            properties: this.portraitSectionProperties(),
            children: portraitChildren,
          });
        }

        documentSections.push({
          properties: this.landscapeSectionProperties(),
          children: sectionContent,
        });

        portraitChildren = [];
        return;
      }

      // As in the reference, Introduction opens the Credit Proposal proper:
      // a fresh page (the rest of the signature page stays empty) under the
      // client's name and a "Credit Proposal" title.
      if (Number(section.refValSeq) === this.INTRODUCTION_REF_VAL_SEQ) {
        portraitChildren.push(
          ...this.buildCreditProposalTitle(
            options.institutionName,
            portraitChildren.length > 0,
          ),
        );
      }

      portraitChildren.push(...sectionContent);
    });

    // Close with a trailing portrait section only if it actually holds
    // content — Repayment Plan (2646) has no template-declared order, so it
    // sorts to the very end and otherwise leaves this as a dangling blank
    // page. `documentSections` still needs at least one entry overall, so
    // an entirely empty report falls back to this (empty) section.
    if (portraitChildren.length > 0 || documentSections.length === 0) {
      documentSections.push({
        properties: this.portraitSectionProperties(),
        children: portraitChildren,
      });
    }

    // Annexure-III — Profile of Board of Directors (a), List of Key
    // Management (b) and Committee Details (c) — native docx tables placed
    // immediately before the Financial Analysis section, mirroring the
    // reference document. They share one section (a new section always
    // starts a new page), so as many tables as fit sit on the same page.
    // Each table is only rendered when the caller supplied rows.
    const annexureThreeTables: Array<Array<Paragraph | Table>> = [];

    if (options.boardOfDirectors && options.boardOfDirectors.length > 0) {
      annexureThreeTables.push(
        this.buildBoardOfDirectorsSection(options.boardOfDirectors),
      );
    }

    if (options.keyManagement && options.keyManagement.length > 0) {
      annexureThreeTables.push(
        this.buildKeyManagementSection(options.keyManagement),
      );
    }

    if (options.committees && options.committees.length > 0) {
      annexureThreeTables.push(
        this.buildCommitteeDetailsSection(options.committees),
      );
    }

    if (options.organogram?.dataUrl) {
      const organogramSection = await this.buildOrganogramSection(
        options.organogram,
      );

      if (organogramSection.length > 0) {
        annexureThreeTables.push(organogramSection);
      }
    }

    if (options.attachments && options.attachments.length > 0) {
      annexureThreeTables.push(
        await this.buildAttachmentsSection(options.attachments),
      );
    }

    if (annexureThreeTables.length > 0) {
      documentSections.push({
        properties: this.portraitSectionProperties(),
        children: annexureThreeTables.flatMap((table, index) => [
          // Gap between tables (adjacent docx tables would merge into one).
          ...(index > 0
            ? [new Paragraph({ spacing: { after: 240 }, children: [] })]
            : []),
          ...table,
        ]),
      });
    }

    // Financial Analysis — always the last section in the document,
    // immediately after Repayment Plan, mirroring its on-screen position.
    // Independent of `reportSections`; only rendered when the caller
    // actually supplied summary rows.
    if (options.financialAnalysisSummaryRows && options.financialAnalysisSummaryRows.length > 0) {
      documentSections.push({
        properties: this.financialAnalysisSectionProperties(),
        children: this.buildFinancialAnalysisSummarySection(
          options.financialAnalysisSummaryRows,
          options.financialAnalysisSummaryColumns ?? [],
          remainingSections.length + 1,
          this.sectionHeadingLabel(
            remainingSections,
            remainingSections.length,
            headingLetterOffset,
          ),
        ),
      });
    }

    // Cover page — always the very first page, mirroring page 1 of the
    // reference Credit Proposal document.
    // The programme line shows the same institution (customer) name as the
    // Executive Summary heading, unless the caller sets programmeName itself.
    if (options.coverPage !== false) {
      const coverInstitutionName = String(options.institutionName ?? '').trim();

      documentSections.unshift(
        await this.buildCoverPageSection({
          ...(coverInstitutionName ? { programmeName: coverInstitutionName } : {}),
          ...(options.coverPage ?? {}),
        }),
      );
    }

    const doc = new Document({
      styles: this.baseStyles(),
      numbering: {
        // One config entry per independent numbered-list block (see
        // nextNumberingReference()) so each list restarts at 1 instead of
        // all numbered lists in the document sharing a single counter.
        config: this.numberingReferences.map((reference) => ({
          reference,
          levels: [
            {
              level: 0,
              format: LevelFormat.DECIMAL,
              text: '%1.',
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: {
                    left: convertInchesToTwip(0.28),
                    hanging: convertInchesToTwip(0.18),
                  },
                },
              },
            },
          ],
        })),
      },
      sections: documentSections,
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${this.safeFileName(fileName)}.docx`);
  }

  /* ====================================================== */
  /* COVER PAGE                                            */
  /* ====================================================== */

  /**
   * Rebuilds page 1 of the reference document paragraph-for-paragraph: every
   * paragraph carries the same single-line box border (so Word merges them
   * into one frame), white shading, Calibri, 1.15 line spacing and the exact
   * font sizes / blank-line spacing of the original.
   */
  private async buildCoverPageSection(
    overrides: CreditProposalCoverPageOptions,
  ): Promise<DocumentSectionEntry> {
    const cover = { ...DEFAULT_COVER_PAGE, ...overrides };
    const logoBytes = await this.loadCoverLogo();

    const box = (
      size: number | undefined,
      bold: boolean,
      options: {
        center?: boolean;
        after?: number;
        italics?: boolean;
        tabs?: boolean;
        children?: Array<TextRun | ImageRun>;
      } = {},
    ): Paragraph =>
      new Paragraph({
        border: {
          top: { style: BorderStyle.SINGLE, size: 4, space: 1, color: 'auto' },
          left: { style: BorderStyle.SINGLE, size: 4, space: 4, color: 'auto' },
          bottom: { style: BorderStyle.SINGLE, size: 4, space: 23, color: 'auto' },
          right: { style: BorderStyle.SINGLE, size: 4, space: 4, color: 'auto' },
        },
        shading: { type: ShadingType.CLEAR, color: 'auto', fill: WHITE },
        spacing: {
          before: 0,
          after: options.after ?? 160,
          line: 276,
          lineRule: LineRuleType.AUTO,
        },
        contextualSpacing: false,
        alignment: options.center === false ? AlignmentType.LEFT : AlignmentType.CENTER,
        tabStops: options.tabs
          ? [
              { type: TabStopType.LEFT, position: 3675 },
              { type: TabStopType.CENTER, position: 4680 },
            ]
          : undefined,
        run: { font: COVER_FONT, size, bold, italics: options.italics },
        children: options.children ?? [],
      });

    const text = (value: string, size: number): TextRun =>
      new TextRun({ text: value, font: COVER_FONT, size, bold: true, color: BLACK });

    const children: Paragraph[] = [
      box(48, false, { center: false }),
      box(48, false, {
        children: logoBytes
          ? [
              new ImageRun({
                type: 'png',
                data: logoBytes,
                transformation: {
                  width: COVER_LOGO_WIDTH_PX,
                  height: COVER_LOGO_HEIGHT_PX,
                },
                altText: {
                  name: 'PMIC final logo',
                  description: 'PMIC final logo',
                  title: 'PMIC final logo',
                },
              }),
            ]
          : [],
      }),
      box(48, false),
      box(54, true, { children: [text(cover.title, 54)] }),
      box(54, true),
      box(36, true, { children: [text(cover.programmeName, 36)] }),
      box(36, true),
      ...cover.facilities.map((facility, index) =>
        box(36, true, { children: [text(`${index + 1}. ${facility}`, 36)] }),
      ),
      box(36, true, { after: 0 }),
      box(36, true),
      box(36, true),
      box(36, true),
      box(36, true, { after: 0 }),
      box(40, true, {
        center: false,
        after: 0,
        tabs: true,
        children: [
          new TextRun({ children: [new Tab()], font: COVER_FONT, size: 36, bold: true }),
          text(cover.dateLabel, 40),
        ],
      }),
      box(40, true, { center: false, after: 0, tabs: true, italics: true }),
      box(undefined, true, { children: [text(cover.companyName, 40)] }),
      box(undefined, true),
    ];

    return {
      properties: {
        page: {
          size: {
            width: COVER_PAGE_WIDTH_TWIPS,
            height: COVER_PAGE_HEIGHT_TWIPS,
            orientation: PageOrientation.PORTRAIT,
          },
          margin: {
            top: COVER_PAGE_MARGIN_TWIPS,
            bottom: COVER_PAGE_MARGIN_TWIPS,
            left: COVER_PAGE_MARGIN_TWIPS,
            right: COVER_PAGE_MARGIN_TWIPS,
            header: 720,
            footer: 720,
          },
        },
      },
      children,
    };
  }

  /** Loads the PMIC cover logo; null if unavailable so export never breaks. */
  private async loadCoverLogo(): Promise<ArrayBuffer | null> {
    try {
      const response = await fetch(COVER_LOGO_PATH);
      if (!response.ok) {
        throw new Error(`Logo fetch failed: ${response.status}`);
      }
      return await response.arrayBuffer();
    } catch (error) {
      console.error('Unable to load Credit Proposal cover logo:', error);
      return null;
    }
  }

  private portraitSectionProperties(): DocumentSectionEntry['properties'] {
    return {
      page: {
        size: {
          width: PAGE_WIDTH_TWIPS,
          height: PAGE_HEIGHT_TWIPS,
          orientation: PageOrientation.PORTRAIT,
        },
        margin: {
          top: this.pageMarginTopBottomTwips,
          bottom: this.pageMarginTopBottomTwips,
          left: this.pageMarginLeftRightTwips,
          right: this.pageMarginLeftRightTwips,
          header: convertInchesToTwip(0.3),
          footer: convertInchesToTwip(0.3),
        },
      },
    };
  }

  /**
   * Width/height are swapped from the portrait page so the Repayment Plan
   * table gets the full landscape span; margins are trimmed slightly so
   * more of that width goes to actual table columns.
   */
  private landscapeSectionProperties(): DocumentSectionEntry['properties'] {
    const landscapeMarginTwips = convertInchesToTwip(0.5);

    return {
      page: {
        size: {
          width: PAGE_HEIGHT_TWIPS,
          height: PAGE_WIDTH_TWIPS,
          orientation: PageOrientation.LANDSCAPE,
        },
        margin: {
          top: landscapeMarginTwips,
          bottom: landscapeMarginTwips,
          left: landscapeMarginTwips,
          right: landscapeMarginTwips,
          header: convertInchesToTwip(0.3),
          footer: convertInchesToTwip(0.3),
        },
      },
    };
  }

  /**
   * Financial Analysis is a tall table, so it gets a portrait page with
   * slim margins: the most width the page allows while the whole table
   * still fits on one page.
   */
  private financialAnalysisSectionProperties(): DocumentSectionEntry['properties'] {
    const margin = CreditReportExportService.FINANCIAL_ANALYSIS_MARGIN_TWIPS;

    return {
      page: {
        size: {
          width: PAGE_WIDTH_TWIPS,
          height: PAGE_HEIGHT_TWIPS,
          orientation: PageOrientation.PORTRAIT,
        },
        margin: {
          top: margin,
          bottom: margin,
          left: margin,
          right: margin,
          header: convertInchesToTwip(0.2),
          footer: convertInchesToTwip(0.2),
        },
      },
    };
  }

  private static readonly FINANCIAL_ANALYSIS_MARGIN_TWIPS = convertInchesToTwip(0.4);

  /** Usable width of the dedicated Repayment Plan landscape page, in twips. */
  private get landscapeContentWidthTwips(): number {
    return PAGE_HEIGHT_TWIPS - convertInchesToTwip(0.5) * 2;
  }

  /**
   * Moves the section with `refValSeq` to sit immediately after
   * Introduction (2525) in the printed Word doc — e.g. Introduction as
   * "12." puts the moved section right after at "13.", pushing whatever
   * section originally followed Introduction down to "14." — regardless of
   * where templateOrder/refValSeq sorting originally placed it. A no-op if
   * either section is missing from this proposal's data (nothing removed or
   * duplicated).
   */
  private moveSectionAfterIntroduction(
    sections: ReportSection[],
    refValSeq: number,
  ): ReportSection[] {
    const SECTIONS_AFTER_INTRODUCTION = 1;

    const movedIndex = sections.findIndex(
      (section) => Number(section.refValSeq) === refValSeq,
    );

    if (movedIndex === -1) {
      return sections;
    }

    const introductionIndex = sections.findIndex(
      (section) =>
        Number(section.refValSeq) === this.INTRODUCTION_REF_VAL_SEQ,
    );

    if (
      introductionIndex === -1 ||
      introductionIndex === movedIndex
    ) {
      return sections;
    }

    const reordered = [...sections];
    const [movedSection] = reordered.splice(
      movedIndex,
      1,
    );

    // Re-find Introduction's index after the removal above — splicing out
    // an earlier item shifts every later index down by one.
    const adjustedIntroductionIndex = reordered.findIndex(
      (section) =>
        Number(section.refValSeq) === this.INTRODUCTION_REF_VAL_SEQ,
    );

    reordered.splice(
      adjustedIntroductionIndex + SECTIONS_AFTER_INTRODUCTION,
      0,
      movedSection,
    );

    return reordered;
  }

  /**
   * Resolves page margins and the first-page sidebar/executive column split
   * from the supplied options, so both actually drive the generated layout
   * instead of being fixed module constants.
   */
  private computePageGeometry(options: CreditReportExportOptions): void {
    const marginOverrideInches = Number(options.pageMarginInches);
    const hasUniformOverride =
      Number.isFinite(marginOverrideInches) && marginOverrideInches > 0;

    this.pageMarginTopBottomTwips = convertInchesToTwip(
      hasUniformOverride
        ? marginOverrideInches
        : DEFAULT_PAGE_MARGIN_TOP_BOTTOM_INCHES,
    );

    this.pageMarginLeftRightTwips = convertInchesToTwip(
      hasUniformOverride
        ? marginOverrideInches
        : DEFAULT_PAGE_MARGIN_LEFT_RIGHT_INCHES,
    );

    this.pageContentWidthTwips =
      PAGE_WIDTH_TWIPS - this.pageMarginLeftRightTwips * 2;

    this.pageContentHeightTwips =
      PAGE_HEIGHT_TWIPS - this.pageMarginTopBottomTwips * 2 - 80;

    const highlightsPercent = Number(
      options.highlightsColumnPercent ??
        DEFAULT_HIGHLIGHTS_COLUMN_PERCENT,
    );

    const normalizedPercent =
      Number.isFinite(highlightsPercent) &&
      highlightsPercent > 0 &&
      highlightsPercent < 100
        ? highlightsPercent
        : DEFAULT_HIGHLIGHTS_COLUMN_PERCENT;

    this.highlightsColumnWidthTwips = Math.round(
      this.pageContentWidthTwips * (normalizedPercent / 100),
    );

    this.gutterColumnWidthTwips = Math.round(
      this.pageContentWidthTwips * (GUTTER_COLUMN_PERCENT / 100),
    );

    // Remaining width, not a fixed percentage, so rounding on the other
    // two columns never leaves a stray gap or overflow.
    this.executiveColumnWidthTwips =
      this.pageContentWidthTwips -
      this.highlightsColumnWidthTwips -
      this.gutterColumnWidthTwips;
  }

  /* ====================================================== */
  /* REFERENCE FIRST PAGE                                  */
  /* ====================================================== */

  private buildExecutiveSummaryPage(
    highlightSection: ReportSection | null,
    executiveSections: ReportSection[],
    options: CreditReportExportOptions,
    followingSections: ReportSection[] = [],
  ): { table: Table; movedSectionCount: number } {
    const leftChildren: Array<Paragraph | Table> = [
      this.sidebarHeading(options.highlightsTitle ?? 'Highlights\n(September 2024)'),
    ];

    if (highlightSection) {
      leftChildren.push(...this.buildHighlightSidebar(highlightSection));
    } else {
      leftChildren.push(this.compactBodyParagraph('No highlights available.'));
    }

    const rightChildren: Array<Paragraph | Table> = [
      this.executiveHeader(
        String(options.institutionName ?? '').trim() || 'Institution Name',
      ),
      this.executiveSubHeader(options.executiveSummaryTitle ?? 'Executive Summary'),
    ];

    this.withExecutiveSummaryContext(() => {
      executiveSections.forEach((section, index) => {
        rightChildren.push(this.summarySectionHeading(index, section.title));
        rightChildren.push(...this.buildSectionBody(section));
      });

      if (executiveSections.length === 0) {
        rightChildren.push(this.compactBodyParagraph('No executive summary data available.'));
      }
    });

    // The Highlights sidebar is usually taller than the summary beside it,
    // which used to leave the right half of the page empty. Pull the
    // following plain-text sections up into that space until the two sides
    // are about level.
    const movedSectionCount = this.fillExecutiveColumnGap(
      highlightSection,
      executiveSections,
      followingSections,
      rightChildren,
    );

    const table = new Table({
      width: {
        size: this.pageContentWidthTwips,
        type: WidthType.DXA,
      },
      columnWidths: [
        this.highlightsColumnWidthTwips,
        this.gutterColumnWidthTwips,
        this.executiveColumnWidthTwips,
      ],
      layout: TableLayoutType.FIXED,
      borders: {
        top: NONE_BORDER,
        bottom: NONE_BORDER,
        left: NONE_BORDER,
        right: NONE_BORDER,
        insideHorizontal: NONE_BORDER,
        insideVertical: NONE_BORDER,
      },
      rows: [
        new TableRow({
          cantSplit: false,
          children: [
            new TableCell({
              width: {
                size: this.highlightsColumnWidthTwips,
                type: WidthType.DXA,
              },
              verticalAlign: VerticalAlign.TOP,
              margins: {
                top: 60,
                bottom: 60,
                left: 50,
                right: 70,
              },
              borders: {
                top: NONE_BORDER,
                bottom: NONE_BORDER,
                left: NONE_BORDER,
                // The divider between the sidebar and the rest of the page
                // lives on this border (single line), not on the table's
                // insideVertical -- with a real spacer column, insideVertical
                // would draw a second, redundant line at the gutter/main seam.
                right: MEDIUM_BORDER,
              },
              children: this.ensureCellChildren(leftChildren),
            }),
            new TableCell({
              width: {
                size: this.gutterColumnWidthTwips,
                type: WidthType.DXA,
              },
              verticalAlign: VerticalAlign.TOP,
              margins: {
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
              },
              borders: {
                top: NONE_BORDER,
                bottom: NONE_BORDER,
                left: NONE_BORDER,
                right: NONE_BORDER,
              },
              // Real empty spacer cell -- the gutter is width, not text.
              children: [new Paragraph('')],
            }),
            new TableCell({
              width: {
                size: this.executiveColumnWidthTwips,
                type: WidthType.DXA,
              },
              verticalAlign: VerticalAlign.TOP,
              margins: {
                top: 60,
                bottom: 60,
                left: 120,
                right: 40,
              },
              borders: {
                top: NONE_BORDER,
                bottom: NONE_BORDER,
                left: NONE_BORDER,
                right: NONE_BORDER,
              },
              children: this.ensureCellChildren(rightChildren),
            }),
          ],
        }),
      ],
    });

    return { table, movedSectionCount };
  }

  /**
   * Appends following sections to the right-hand column while it is still
   * clearly shorter than the Highlights sidebar. Only sections that render
   * as plain paragraphs are moved (no tables / captured images, which need
   * the full page width). Returns how many sections were moved.
   */
  private fillExecutiveColumnGap(
    highlightSection: ReportSection | null,
    executiveSections: ReportSection[],
    followingSections: ReportSection[],
    rightChildren: Array<Paragraph | Table>,
  ): number {
    if (!highlightSection || followingSections.length === 0) {
      return 0;
    }

    const sidebarWidth = this.highlightsColumnWidthTwips - 120 - SIDEBAR_INDENT_LEFT;
    const leftHeight = this.highlightSidebarEntries(highlightSection).reduce(
      (total, entry) =>
        total +
        this.estimateWrappedHeight(
          [`- ${entry.title}${entry.content ? ': ' : ''}${entry.content}`],
          sidebarWidth,
          100,
          250,
          SIDEBAR_SPACING_AFTER,
        ),
      SIDEBAR_HEADING_LINE * 2 + SIDEBAR_HEADING_AFTER,
    );

    const execWidth = this.executiveColumnWidthTwips - 160;
    let rightHeight = 1100;

    executiveSections.forEach((section) => {
      rightHeight +=
        300 +
        this.estimateWrappedHeight(
          this.sectionTextLines(section),
          execWidth,
          108,
          EXEC_BODY_LINE_SPACING,
          EXEC_BODY_SPACING_AFTER,
        );
    });

    let moved = 0;

    for (const [index, section] of followingSections.entries()) {
      // Stay clearly under the sidebar height: a section that overshoots
      // would just move the empty space to the left column.
      if (rightHeight + 600 >= leftHeight) {
        break;
      }

      const content = this.buildSection(
        section,
        index + 1,
        this.sectionHeadingLabel(followingSections, index, executiveSections.length),
      );

      if (
        this.sectionCaptureKey(section) ||
        content.some((item) => item instanceof Table)
      ) {
        break;
      }

      rightChildren.push(...content);
      rightHeight +=
        500 +
        this.estimateWrappedHeight(
          this.sectionTextLines(section),
          execWidth,
          115,
          BODY_LINE_SPACING + 40,
          BODY_SPACING_AFTER,
        );
      moved += 1;
    }

    return moved;
  }

  private buildHighlightSidebar(section: ReportSection): Paragraph[] {
    return this.highlightSidebarEntries(section).map((entry) =>
      this.sidebarItem(entry.title, entry.content),
    );
  }

  private highlightSidebarEntries(
    section: ReportSection,
  ): Array<{ title: string; content: string }> {
    const entries: Array<{ title: string; content: string }> = [];

    section.blocks.forEach((block) => {
      if (Array.isArray(block.jsonData) && block.jsonData.length > 0) {
        block.jsonData.forEach((item) => {
          const title = this.normalizeSidebarText(item.title);
          const rawContent = this.getHighlightItemContent(item);
          // Rich text: one sidebar line per paragraph / list item ("• " kept);
          // plain text stays a single line as before.
          const contentLines = isRichTextHtml(rawContent)
            ? richTextToPlainText(rawContent)
                .split('\n')
                .map((line) => this.normalizeSidebarText(line))
                .filter(Boolean)
            : [this.normalizeSidebarText(rawContent)].filter(Boolean);
          const bullets = Array.isArray(item.bullets)
            ? item.bullets
                .map((bullet) => this.normalizeSidebarText(bullet))
                .filter(Boolean)
            : [];

          if (title || contentLines.length) {
            entries.push({ title, content: contentLines[0] ?? '' });
          }

          contentLines.slice(1).forEach((line) => {
            entries.push({ title: '', content: line });
          });

          bullets.forEach((bullet) => {
            entries.push({ title: '', content: bullet });
          });
        });
      }

      const fallback = this.normalizeSidebarText(block.txtData);
      if (fallback) {
        entries.push({ title: '', content: fallback });
      }
    });

    return entries;
  }

  /* ---- Rough height estimates (twips), used only to balance the first page ---- */

  private estimateWrappedHeight(
    lines: string[],
    widthTwips: number,
    charTwips: number,
    lineTwips: number,
    afterTwips: number,
  ): number {
    const charsPerLine = Math.max(8, Math.floor(widthTwips / charTwips));

    return lines.reduce(
      (total, line) =>
        total +
        Math.max(1, Math.ceil(line.length / charsPerLine)) * lineTwips +
        afterTwips,
      0,
    );
  }

  /** Every text line of a section's blocks (rich text flattened), for height estimation. */
  private sectionTextLines(section: ReportSection): string[] {
    const lines: string[] = [];

    const collect = (value: unknown): void => {
      if (typeof value === 'string') {
        const text = isRichTextHtml(value) ? richTextToPlainText(value) : value;
        text
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean)
          .forEach((line) => lines.push(line));
      } else if (Array.isArray(value)) {
        value.forEach(collect);
      } else if (value && typeof value === 'object') {
        Object.values(value as Record<string, unknown>).forEach(collect);
      }
    };

    section.blocks.forEach((block) => {
      collect(block.txtData);
      collect(block.jsonData);
    });

    return lines;
  }

  /**
   * One complete highlight record must stay one Word Paragraph -- Word
   * wraps it naturally across the (now wider) sidebar column. Accidental
   * newlines/tabs from backend content are what forced manual, overly
   * frequent line breaks; collapse them to single spaces instead.
   */
  private normalizeSidebarText(value: unknown): string {
    return String(value ?? '')
      .replace(/\r\n/g, '\n')
      .replace(/\n+/g, ' ')
      .replace(/[ \t]+/g, ' ')
      .trim();
  }

  private sidebarHeading(text: string): Paragraph {
    const lines = String(text)
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    const children: TextRun[] = [];

    lines.forEach((line, index) => {
      children.push(
        new TextRun({
          text: line,
          bold: index === 0,
          font: FONT,
          size: SIDEBAR_HEADING_SIZE,
          color: BLACK,
          break: index > 0 ? 1 : undefined,
        }),
      );
    });

    return new Paragraph({
      alignment: AlignmentType.CENTER,
      contextualSpacing: false,
      spacing: {
        before: 0,
        after: SIDEBAR_HEADING_AFTER,
        line: SIDEBAR_HEADING_LINE,
        lineRule: LineRuleType.AUTO,
      },
      children,
    });
  }

  private sidebarItem(title: string, content: string): Paragraph {
    const runs: TextRun[] = [
      new TextRun({
        text: '- ',
        font: FONT,
        size: SIDEBAR_TEXT_SIZE,
        color: BLACK,
      }),
    ];

    if (title) {
      runs.push(
        new TextRun({
          text: `${title}${content ? ': ' : ''}`,
          bold: true,
          font: FONT,
          size: SIDEBAR_TEXT_SIZE,
          color: BLACK,
        }),
      );
    }

    if (content) {
      runs.push(
        new TextRun({
          text: content,
          font: FONT,
          size: SIDEBAR_TEXT_SIZE,
          color: BLACK,
        }),
      );
    }

    // Left-aligned (not justified): justifying short highlight lines
    // creates unnaturally stretched word gaps in a narrow sidebar column.
    // A small hanging indent keeps wrapped lines aligned with the text
    // instead of the leading bullet.
    return new Paragraph({
      alignment: AlignmentType.LEFT,
      contextualSpacing: false,
      indent: {
        left: SIDEBAR_INDENT_LEFT,
        hanging: SIDEBAR_INDENT_HANGING,
      },
      spacing: {
        before: 0,
        after: SIDEBAR_SPACING_AFTER,
        line: SIDEBAR_LINE_SPACING,
        lineRule: LineRuleType.AUTO,
      },
      children: runs,
    });
  }

  private executiveHeader(text: string): Paragraph {
    return new Paragraph({
      keepNext: true,
      contextualSpacing: false,
      alignment: AlignmentType.CENTER,
      spacing: { before: 0, after: 18, line: MAIN_HEADING_LINE, lineRule: LineRuleType.AUTO },
      border: {
        top: THIN_BORDER,
        bottom: THIN_BORDER,
      },
      children: [
        new TextRun({
          text,
          bold: true,
          font: FONT,
          size: INSTITUTION_NAME_SIZE,
          color: BLACK,
        }),
      ],
    });
  }

  private executiveSubHeader(text: string): Paragraph {
    return new Paragraph({
      keepNext: true,
      contextualSpacing: false,
      alignment: AlignmentType.CENTER,
      spacing: { before: 0, after: 80, line: MAIN_HEADING_LINE, lineRule: LineRuleType.AUTO },
      border: {
        bottom: THIN_BORDER,
      },
      children: [
        new TextRun({
          text,
          bold: true,
          font: FONT,
          size: EXEC_SUMMARY_TITLE_SIZE,
          color: BLACK,
        }),
      ],
    });
  }

  private summarySectionHeading(index: number, title: string): Paragraph {
    const prefix = this.alphabeticPrefix(index);

    return new Paragraph({
      keepNext: true,
      keepLines: true,
      contextualSpacing: false,
      alignment: AlignmentType.LEFT,
      spacing: {
        before: EXEC_SUBHEADING_BEFORE,
        after: EXEC_SUBHEADING_AFTER,
        line: MAIN_HEADING_LINE,
        lineRule: LineRuleType.AUTO,
      },
      children: [
        new TextRun({
          text: `${prefix}) ${String(title ?? '').trim()}:`,
          bold: true,
          font: FONT,
          size: EXEC_SUBHEADING_SIZE,
          color: BLACK,
        }),
      ],
    });
  }

  /* ====================================================== */
  /* NORMAL SECTION DISPATCH                               */
  /* ====================================================== */

  private buildSection(
    section: ReportSection,
    sectionNumber: number,
    label?: string,
  ): Array<Paragraph | Table> {
    const body = this.buildSectionBody(section);
    const captureKey = this.sectionCaptureKey(section);
    const imageParagraphs =
      captureKey && this.capturedTableImageParagraphs.get(captureKey);

    // A captured image already carries its annexure label; the native table
    // fallback gets the same label so the numbering never depends on capture.
    return [
      this.sectionTitle(section.title, sectionNumber, label),
      ...(imageParagraphs
        ? this.replaceTablesWithImage(body, imageParagraphs)
        : [
            ...(captureKey ? this.buildAnnexureHeading(captureKey) : []),
            ...body,
          ]),
      // The reference prints the MRC / BRC signature page right under the
      // Summary Terms and Conditions table.
      ...(Number(section.refValSeq) === SUMMARY_TERMS_REF_VAL_SEQ
        ? this.buildRecommendationSignatures()
        : []),
    ];
  }

  /**
   * Centred client name over "Credit Proposal", both bold, heading the page
   * Introduction starts on. `newPage` false when nothing precedes it, so
   * the document never opens with a blank page.
   */
  private buildCreditProposalTitle(
    institutionName: string | undefined,
    newPage: boolean,
  ): Paragraph[] {
    const name = String(institutionName ?? '').trim();

    const title = (text: string, after: number, pageBreakBefore = false) =>
      new Paragraph({
        pageBreakBefore,
        keepNext: true,
        alignment: AlignmentType.CENTER,
        contextualSpacing: false,
        spacing: { before: 0, after },
        children: [
          new TextRun({ text, bold: true, font: FONT, color: BLACK, size: MAIN_HEADING_SIZE }),
        ],
      });

    return [
      ...(name ? [title(name, 80, newPage)] : []),
      title('Credit Proposal', 120, newPage && !name),
    ];
  }

  /**
   * MRC / BRC recommendation signatures as in the reference: a bullet
   * caption, room to sign, then each signatory's bold name over its title,
   * centred in borderless columns (MRC: three across ~94% of the width, BRC:
   * four across the full width, CEO alone in the centre).
   */
  private buildRecommendationSignatures(): Array<Paragraph | Table> {
    const width = this.pageContentWidthTwips;
    const output: Array<Paragraph | Table> = [];

    const cellBorders = {
      top: NONE_BORDER,
      bottom: NONE_BORDER,
      left: NONE_BORDER,
      right: NONE_BORDER,
    };
    const noBorders = {
      ...cellBorders,
      insideHorizontal: NONE_BORDER,
      insideVertical: NONE_BORDER,
    };

    const line = (text: string, bold: boolean) =>
      new Paragraph({
        alignment: AlignmentType.CENTER,
        contextualSpacing: false,
        spacing: { before: 0, after: 0, line: 252, lineRule: LineRuleType.AUTO },
        children: [
          new TextRun({ text, bold, font: FONT, color: BLACK, size: SIGNATURE_TEXT_SIZE }),
        ],
      });

    RECOMMENDATION_SIGNATURE_BLOCKS.forEach((block, blockIndex) => {
      if (block.caption) {
        output.push(
          new Paragraph({
            // The whole signature page starts on a fresh page so no block is
            // split across a page break; keepNext holds the rest to it.
            pageBreakBefore: blockIndex === 0,
            keepNext: true,
            contextualSpacing: false,
            spacing: { before: blockIndex === 0 ? 0 : 900, after: 0 },
            indent: { left: 540, hanging: 270 },
            children: [
              new TextRun({ text: `•\t${block.caption}`, font: FONT, color: BLACK, size: SIGNATURE_TEXT_SIZE }),
            ],
            tabStops: [{ type: TabStopType.LEFT, position: 540 }],
          }),
        );
      }

      // Room to sign above the names.
      output.push(
        new Paragraph({
          keepNext: true,
          spacing: { before: 1100, after: 0 },
          children: [new TextRun({ text: '', size: SIGNATURE_TEXT_SIZE })],
        }),
      );

      const count = block.signatories.length;
      const tableWidth =
        count === 1 ? width : count === 3 ? Math.round(width * 0.94) : width;
      const columnWidth = Math.floor(tableWidth / count);

      output.push(
        new Table({
          width: { size: columnWidth * count, type: WidthType.DXA },
          layout: TableLayoutType.FIXED,
          columnWidths: block.signatories.map(() => columnWidth),
          borders: noBorders,
          rows: [
            new TableRow({
              cantSplit: true,
              children: block.signatories.map(
                (signatory) =>
                  new TableCell({
                    width: { size: columnWidth, type: WidthType.DXA },
                    borders: cellBorders,
                    margins: { top: 0, bottom: 0, left: 60, right: 60 },
                    children: [
                      line(signatory.name, true),
                      ...signatory.titleLines.map((title) => line(title, false)),
                    ],
                  }),
              ),
            }),
          ],
        }),
      );
    });

    return output;
  }

  /**
   * Maps a report section to its captured-table key, mirroring the dispatch
   * order in buildSectionBody so a section is classified exactly the same
   * way it is rendered. Returns null for sections that are not one of the
   * six snapshotted tables.
   */
  private sectionCaptureKey(section: ReportSection): CapturedTableKey | null {
    const ref = Number(section.refValSeq);

    if (ref === this.FINANCIAL_ANALYSIS_DETAILS_REF_VAL_SEQ) {
      return 'financial-analysis-details';
    }
    if (ref === this.REPAYMENT_PLAN_REF_VAL_SEQ) {
      return 'repayment-plan';
    }
    if (ref === this.DISBURSEMENT_REPAYMENT_SUMMARY_REF_VAL_SEQ) {
      return 'disbursement-summary';
    }
    if (this.getNestedReportBlocks(section).length > 0) {
      return null;
    }
    if (this.getRecommendationRows(section).length > 0) {
      return 'recommendation';
    }
    if (this.getInstitutionalRatingData(section)) {
      return 'institutional-rating';
    }

    return null;
  }

  /**
   * Replaces the native docx table(s) in a section body with the captured
   * image paragraphs, inserted at the position of the first table. All
   * surrounding paragraphs (headings, captions, intros, conclusions) are
   * preserved; any additional tables in the same body are dropped so the
   * table content is not duplicated.
   */
  private replaceTablesWithImage(
    body: Array<Paragraph | Table>,
    imageParagraphs: Paragraph[],
  ): Array<Paragraph | Table> {
    const output: Array<Paragraph | Table> = [];
    let inserted = false;

    for (const item of body) {
      if (item instanceof Table) {
        if (!inserted) {
          output.push(...imageParagraphs);
          inserted = true;
        }
        continue;
      }

      output.push(item);
    }

    if (!inserted) {
      output.push(...imageParagraphs);
    }

    return output;
  }

  /* ====================================================== */
  /* CAPTURED TABLE IMAGES → PAGE-SLICED IMAGE PARAGRAPHS     */
  /* ====================================================== */

  /** 1 inch = 1440 twips = 96 CSS px, so 1 CSS px = 15 twips. */
  private static readonly TWIPS_PER_PX = 15;

  /** Leave a hair of headroom so rounding never spills a slice onto a new page. */
  private static readonly PAGE_FILL_RATIO = 0.98;

  /** Gap between two captured tables that share a page (e.g. Conventional / Islamic). */
  private static readonly TABLE_GAP_TWIPS = convertInchesToTwip(0.4);

  /**
   * A table that does not fit in the room left on the page is only started
   * there when at least this share of a page is free; otherwise it begins
   * on the next page instead of leaving a sliver at the bottom.
   */
  private static readonly MIN_FIRST_SLICE_RATIO = 0.2;

  /**
   * Render captured tables a little inside the page's usable width so there
   * is always visible left/right breathing room and every column stays on
   * the page (a table sized to the exact margin edge reads as clipped).
   */
  private static readonly WIDTH_FIT_RATIO = 0.94;

  /**
   * Annexure label shown above a captured table image:
   *   Annexure-I       Tentative Disbursements and Repayment Plan (summary)
   *   Annexure-II      Tentative Repayment Plan
   *   Annexure-III (a) Profile of Board of Directors (buildBoardOfDirectorsSection)
   *   Annexure-III (b) List of Key Management (buildKeyManagementSection)
   *   Annexure-IV      Financial Analysis
   * Financial Analysis Details, Institutional Rating and Recommendation are
   * main-body tables and carry no annexure label.
   */
  private buildAnnexureHeading(key: CapturedTableKey): Paragraph[] {
    let label = '';

    switch (key) {
      case 'disbursement-summary':
        label = 'Annexure-I';
        break;
      case 'repayment-plan':
        label = 'Annexure-II';
        break;
      case 'financial-analysis-summary':
        label = 'Annexure-IV';
        break;
      default:
        return [];
    }

    // Mirror the reference document: the annexure label sits at the
    // top-right above the table (title only, no caption). keepNext moves it
    // to the next page together with the table when the table doesn't fit.
    return [
      new Paragraph({
        alignment: AlignmentType.RIGHT,
        keepNext: true,
        spacing: { before: 160, after: 60 },
        children: [
          new TextRun({ text: label, bold: true, size: SUB_HEADING_SIZE }),
        ],
      }),
    ];
  }

  /**
   * Profile of Board of Directors table (Annexure-III (a)) as a native docx
   * table, mirroring the reference document: an "Annexure-III (a)" label at
   * the top-right, the "Profile of Board of Directors" heading, then the
   * five-column table (S.# / Name-Position / Qualification / Committee member
   * / Experience and current position).
   */
  private buildBoardOfDirectorsSection(
    rows: BoardDirectorReportRow[],
  ): Array<Paragraph | Table> {
    const output: Array<Paragraph | Table> = [
      new Paragraph({
        alignment: AlignmentType.RIGHT,
        keepNext: true,
        spacing: { before: 160, after: 40 },
        children: [
          new TextRun({
            text: 'Annexure-III (a)',
            bold: true,
            size: SUB_HEADING_SIZE,
          }),
        ],
      }),
      new Paragraph({
        keepNext: true,
        spacing: { after: 80 },
        children: [
          new TextRun({
            text: 'Profile of Board of Directors',
            bold: true,
            size: MAIN_HEADING_SIZE,
          }),
        ],
      }),
    ];

    const tableRows: TableRow[] = [
      new TableRow({
        tableHeader: true,
        children: [
          this.headerCell('S.#'),
          this.headerCell('Name/Position'),
          this.headerCell('Qualification'),
          this.headerCell('Committee member'),
          this.headerCell('Experience and current position'),
        ],
      }),
    ];

    rows.forEach((row, index) => {
      const nameParagraphs: Paragraph[] = [
        new Paragraph({
          spacing: { before: 0, after: 0, line: TABLE_LINE_SPACING, lineRule: LineRuleType.AUTO },
          children: [
            new TextRun({ text: row.name || '-', bold: true, size: TABLE_TEXT_SIZE }),
          ],
        }),
      ];

      if (row.position) {
        nameParagraphs.push(
          new Paragraph({
            spacing: { before: 0, after: 0, line: TABLE_LINE_SPACING, lineRule: LineRuleType.AUTO },
            children: [
              new TextRun({ text: `(${row.position})`, size: TABLE_TEXT_SIZE }),
            ],
          }),
        );
      }

      tableRows.push(
        new TableRow({
          children: [
            this.bodyCell(String(index + 1), { center: true }),
            new TableCell({
              verticalAlign: VerticalAlign.TOP,
              margins: this.cellMargins(),
              children: nameParagraphs,
            }),
            this.bodyCell(row.qualification || '-'),
            this.bodyCell(row.committee || '-'),
            this.bodyCell(row.experience || '-'),
          ],
        }),
      );
    });

    const w = this.pageContentWidthTwips;
    output.push(
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        columnWidths: [
          Math.round(w * 0.06),
          Math.round(w * 0.2),
          Math.round(w * 0.2),
          Math.round(w * 0.19),
          Math.round(w * 0.35),
        ],
        layout: TableLayoutType.FIXED,
        rows: tableRows,
        borders: this.tableBorders(),
      }),
    );

    return output;
  }

  /**
   * List of Key Management table (Annexure-III (b)) as a native docx table,
   * mirroring the reference document: an "Annexure-III (b)" label at the
   * top-right, the "List of Key Management" heading, then the four-column
   * table (Position / Name / Qualification / Experience (brief profile)).
   */
  private buildKeyManagementSection(
    rows: KeyManagementReportRow[],
  ): Array<Paragraph | Table> {
    const output: Array<Paragraph | Table> = [
      new Paragraph({
        alignment: AlignmentType.RIGHT,
        keepNext: true,
        spacing: { before: 160, after: 40 },
        children: [
          new TextRun({
            text: 'Annexure-III (b)',
            bold: true,
            size: SUB_HEADING_SIZE,
          }),
        ],
      }),
      new Paragraph({
        keepNext: true,
        spacing: { after: 80 },
        children: [
          new TextRun({
            text: 'List of Key Management',
            bold: true,
            size: MAIN_HEADING_SIZE,
          }),
        ],
      }),
    ];

    const tableRows: TableRow[] = [
      new TableRow({
        tableHeader: true,
        children: [
          this.headerCell('Position'),
          this.headerCell('Name'),
          this.headerCell('Qualification'),
          this.headerCell('Experience (brief profile)'),
        ],
      }),
    ];

    rows.forEach((row) => {
      tableRows.push(
        new TableRow({
          children: [
            this.bodyCell(row.position || '-', { bold: true }),
            this.bodyCell(row.name || '-'),
            this.bodyCell(row.qualification || '-'),
            this.bodyCell(row.experience || '-'),
          ],
        }),
      );
    });

    const w = this.pageContentWidthTwips;
    output.push(
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        columnWidths: [
          Math.round(w * 0.2),
          Math.round(w * 0.2),
          Math.round(w * 0.25),
          Math.round(w * 0.35),
        ],
        layout: TableLayoutType.FIXED,
        rows: tableRows,
        borders: this.tableBorders(),
      }),
    );

    return output;
  }

  /**
   * Committee Details table (Annexure-III (c)) as a native docx table: an
   * "Annexure-III (c)" label at the top-right, the "Committee Details"
   * heading, then one row per member (S.# / Committee / Member / Position /
   * Qualification) with the committee cells merged across its members.
   */
  private buildCommitteeDetailsSection(
    committees: CommitteeReportRow[],
  ): Array<Paragraph | Table> {
    const output: Array<Paragraph | Table> = [
      new Paragraph({
        alignment: AlignmentType.RIGHT,
        keepNext: true,
        spacing: { before: 160, after: 40 },
        children: [
          new TextRun({
            text: 'Annexure-III (c)',
            bold: true,
            size: SUB_HEADING_SIZE,
          }),
        ],
      }),
      new Paragraph({
        keepNext: true,
        spacing: { after: 80 },
        children: [
          new TextRun({
            text: 'Committee Details',
            bold: true,
            size: MAIN_HEADING_SIZE,
          }),
        ],
      }),
    ];

    const tableRows: TableRow[] = [
      new TableRow({
        tableHeader: true,
        children: [
          this.headerCell('S.#'),
          this.headerCell('Committee'),
          this.headerCell('Member Name'),
          this.headerCell('Position'),
          this.headerCell('Qualification'),
        ],
      }),
    ];

    committees.forEach((committee, index) => {
      const members =
        committee.members.length > 0
          ? committee.members
          : [{ name: '', position: '', qualification: '' }];
      const rowSpan = members.length;

      members.forEach((member, memberIndex) => {
        tableRows.push(
          new TableRow({
            cantSplit: true,
            children: [
              // S.# and Committee are merged down across the committee's members.
              ...(memberIndex === 0
                ? [
                    this.bodyCell(String(index + 1), { center: true, rowSpan }),
                    this.bodyCell(committee.committee || '-', {
                      bold: true,
                      rowSpan,
                    }),
                  ]
                : []),
              this.bodyCell(member.name || '-'),
              this.bodyCell(member.position || '-'),
              this.bodyCell(member.qualification || '-'),
            ],
          }),
        );
      });
    });

    const w = this.pageContentWidthTwips;
    output.push(
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        columnWidths: [
          Math.round(w * 0.06),
          Math.round(w * 0.24),
          Math.round(w * 0.25),
          Math.round(w * 0.22),
          Math.round(w * 0.23),
        ],
        layout: TableLayoutType.FIXED,
        rows: tableRows,
        borders: this.tableBorders(),
      }),
    );

    return output;
  }

  /**
   * Organogram (Annexure-III (d)): an "Annexure-III (d)" label at the
   * top-right, a centred "Organogram" heading, then the uploaded image
   * scaled to fit the page (never enlarged). Returns nothing when the image
   * cannot be read, so the report still exports without it.
   */
  private async buildOrganogramSection(
    organogram: OrganogramReportImage,
  ): Promise<Paragraph[]> {
    if (typeof document === 'undefined') {
      return [];
    }

    let image: HTMLImageElement;

    try {
      image = await this.loadImageElement(organogram.dataUrl);
    } catch (error) {
      console.error('Failed to read the organogram image:', error);
      return [];
    }

    if (!image.naturalWidth || !image.naturalHeight) {
      return [];
    }

    const pxPerTwip = 1 / CreditReportExportService.TWIPS_PER_PX;
    const maxWidthPx = this.pageContentWidthTwips * pxPerTwip;
    // Leave room for the label and heading above the image.
    const maxHeightPx =
      (this.pageContentHeightTwips - convertInchesToTwip(1.2)) * pxPerTwip;
    const scale = Math.min(
      1,
      maxWidthPx / image.naturalWidth,
      maxHeightPx / image.naturalHeight,
    );

    return [
      new Paragraph({
        alignment: AlignmentType.RIGHT,
        keepNext: true,
        spacing: { before: 160, after: 40 },
        children: [
          new TextRun({
            text: 'Annexure-III (d)',
            bold: true,
            size: SUB_HEADING_SIZE,
          }),
        ],
      }),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        keepNext: true,
        spacing: { after: 160 },
        children: [
          new TextRun({
            text: 'Organogram',
            bold: true,
            size: MAIN_HEADING_SIZE,
          }),
        ],
      }),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 0 },
        children: [
          new ImageRun({
            type: organogram.type,
            data: this.dataUrlToUint8Array(organogram.dataUrl),
            transformation: {
              width: Math.round(image.naturalWidth * scale),
              height: Math.round(image.naturalHeight * scale),
            },
          }),
        ],
      }),
    ];
  }

  /**
   * "Attachments" section after the Organogram: one entry per document
   * uploaded on the Attachments tab, titled by its document type. An image
   * is embedded (scaled to fit the page, never enlarged); any other file
   * (PDF, Word, Excel, ...) cannot be rendered inside the Word document, so
   * it is listed by its file name.
   */
  private async buildAttachmentsSection(
    attachments: AttachmentReportItem[],
  ): Promise<Paragraph[]> {
    const output: Paragraph[] = [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        keepNext: true,
        spacing: { before: 160, after: 160 },
        children: [
          new TextRun({
            text: 'Attachments',
            bold: true,
            size: MAIN_HEADING_SIZE,
          }),
        ],
      }),
    ];

    const pxPerTwip = 1 / CreditReportExportService.TWIPS_PER_PX;
    const maxWidthPx = this.pageContentWidthTwips * pxPerTwip;
    // Leave room for the attachment's title above the image.
    const maxHeightPx =
      (this.pageContentHeightTwips - convertInchesToTwip(0.8)) * pxPerTwip;

    for (const [index, attachment] of attachments.entries()) {
      output.push(
        new Paragraph({
          keepNext: true,
          spacing: { before: index > 0 ? 240 : 0, after: 80 },
          children: [
            new TextRun({
              text: `${index + 1}. ${attachment.title || attachment.fileName || 'Attachment'}`,
              bold: true,
              size: SUB_HEADING_SIZE,
            }),
          ],
        }),
      );

      let imageParagraph: Paragraph | null = null;

      if (attachment.image?.dataUrl && typeof document !== 'undefined') {
        try {
          const image = await this.loadImageElement(attachment.image.dataUrl);

          if (image.naturalWidth && image.naturalHeight) {
            const scale = Math.min(
              1,
              maxWidthPx / image.naturalWidth,
              maxHeightPx / image.naturalHeight,
            );

            imageParagraph = new Paragraph({
              alignment: AlignmentType.CENTER,
              spacing: { after: 0 },
              children: [
                new ImageRun({
                  type: attachment.image.type,
                  data: this.dataUrlToUint8Array(attachment.image.dataUrl),
                  transformation: {
                    width: Math.round(image.naturalWidth * scale),
                    height: Math.round(image.naturalHeight * scale),
                  },
                }),
              ],
            });
          }
        } catch (error) {
          console.error('Failed to read an attachment image:', error);
        }
      }

      output.push(
        imageParagraph ??
          this.compactBodyParagraph(`File: ${attachment.fileName || '-'}`),
      );
    }

    return output;
  }

  private async prepareCapturedTableImageParagraphs(
    images?: CapturedTableImages,
  ): Promise<Map<CapturedTableKey, Paragraph[]>> {
    const prepared = new Map<CapturedTableKey, Paragraph[]>();

    if (!images || typeof document === 'undefined') {
      return prepared;
    }

    // A docx landscape section stores pgSz as w:w = PAGE_WIDTH_TWIPS (the
    // 8.5in edge) and w:h = PAGE_HEIGHT_TWIPS (the 11in edge) plus an
    // orientation flag, and Word lays fixed-size content (an embedded image)
    // out against those stored w:w / w:h — so the usable *width* is the 8.5in
    // edge minus margins (sizing to the 11in edge is what pushed the
    // right-hand columns off the page) and the usable *height* is the full
    // 11in edge minus margins. Slicing to the short edge (as before) left a
    // large empty band at the bottom of every landscape page.
    const landscapeMarginTwips = convertInchesToTwip(0.5);
    const landscapeWidthTwips = PAGE_WIDTH_TWIPS - landscapeMarginTwips * 2;
    const landscapeHeightTwips =
      PAGE_HEIGHT_TWIPS - landscapeMarginTwips * 2 - 80;

    for (const key of Object.keys(images) as CapturedTableKey[]) {
      const entry = images[key];
      const parts = (Array.isArray(entry) ? entry : [entry]).filter(
        (part): part is CapturedTableImage =>
          !!part?.dataUrl && !!part.width && !!part.height,
      );

      if (parts.length === 0) {
        continue;
      }

      const isLandscape = key === 'repayment-plan';

      const isFinancialAnalysis = key === 'financial-analysis-summary';
      const financialMarginTwips =
        CreditReportExportService.FINANCIAL_ANALYSIS_MARGIN_TWIPS;

      const contentWidthTwips = isFinancialAnalysis
        ? PAGE_WIDTH_TWIPS - financialMarginTwips * 2
        : isLandscape
          ? landscapeWidthTwips
          : this.pageContentWidthTwips;

      const contentHeightTwips = isFinancialAnalysis
        ? PAGE_HEIGHT_TWIPS - financialMarginTwips * 2 - 80
        : isLandscape
          ? landscapeHeightTwips
          : this.pageContentHeightTwips;

      // Reference-style annexure label + caption placed above the table
      // image; the first page must also fit these plus the section title.
      const annexureHeading = this.buildAnnexureHeading(key);
      const topAllowanceTwips = convertInchesToTwip(
        annexureHeading.length > 0 ? 1.15 : 0.6,
      );

      try {
        const paragraphs: Paragraph[] = [];
        // Space taken on the current page above the next table: the titles
        // for the first table, then whatever the previous table left used.
        let usedTwips = topAllowanceTwips;

        for (const [index, image] of parts.entries()) {
          // Further tables follow on the same page after a small gap, and
          // only move to a new page when too little room is left.
          if (index > 0) {
            paragraphs.push(
              new Paragraph({ spacing: { after: 240 }, children: [] }),
            );
            usedTwips += CreditReportExportService.TABLE_GAP_TWIPS;
          }

          // The last Repayment Plan image is the consolidated table; it gets
          // its own heading (the images before it are the per-type tables).
          if (
            key === 'repayment-plan' &&
            parts.length > 1 &&
            index === parts.length - 1
          ) {
            paragraphs.push(this.subHeading(CONSOLIDATED_REPAYMENT_PLAN_TITLE));
            usedTwips += 500;
          }

          const sliced = await this.buildSlicedImageParagraphs(
            image,
            contentWidthTwips,
            contentHeightTwips,
            usedTwips,
            key === 'financial-analysis-summary',
          );

          // The annexure label goes right above the first table, after its
          // page break when that table starts on a fresh page, so the label
          // never stays behind on the previous page.
          if (index === 0 && sliced.startsOnNewPage) {
            const [pageBreak, ...rest] = sliced.paragraphs;
            paragraphs.push(pageBreak, ...annexureHeading, ...rest);
          } else if (index === 0) {
            paragraphs.push(...annexureHeading, ...sliced.paragraphs);
          } else {
            paragraphs.push(...sliced.paragraphs);
          }
          usedTwips = sliced.lastPageUsedTwips;
        }

        if (paragraphs.length > 0) {
          prepared.set(key, paragraphs);
        }
      } catch (error) {
        console.error(
          `Failed to prepare captured table image for "${key}":`,
          error,
        );
      }
    }

    return prepared;
  }

  /**
   * Fits a captured table image to the page content width (never upscaling a
   * table that is already narrower than the page, so its rendered width is
   * never reduced or artificially inflated) and, when the fitted image is
   * taller than one page, slices it into page-height strips — each strip on
   * its own page — so no row is squeezed and the table simply continues on
   * the next page.
   */
  private async buildSlicedImageParagraphs(
    image: CapturedTableImage,
    contentWidthTwips: number,
    contentHeightTwips: number,
    topAllowanceTwips: number,
    fitOnePage = false,
  ): Promise<{ paragraphs: Paragraph[]; lastPageUsedTwips: number; startsOnNewPage: boolean }> {
    const pxPerTwip = 1 / CreditReportExportService.TWIPS_PER_PX;
    // A one-page table uses the full width; others keep a small side margin.
    const availableWidthPx =
      contentWidthTwips *
      pxPerTwip *
      (fitOnePage ? 1 : CreditReportExportService.WIDTH_FIT_RATIO);
    const availableHeightPx =
      contentHeightTwips * pxPerTwip * CreditReportExportService.PAGE_FILL_RATIO;

    const cssWidthPx =
      image.cssWidth && image.cssWidth > 0 ? image.cssWidth : image.width;

    // Fit to the page width without ever enlarging beyond the table's own
    // on-screen width.
    const aspectRatio = image.height / image.width;
    // A one-page table may be enlarged beyond its on-screen size to fill the
    // page (it is captured at a high scale, so it stays sharp).
    let displayWidthPx = fitOnePage
      ? availableWidthPx
      : Math.min(availableWidthPx, cssWidthPx);

    // fitOnePage: shrink the whole table (width and height together) until
    // it fits on the first page under the title, instead of slicing it over
    // several pages. Small text is acceptable; a split table is not.
    if (fitOnePage) {
      const firstPageFitPx =
        availableHeightPx -
        topAllowanceTwips *
          pxPerTwip *
          CreditReportExportService.PAGE_FILL_RATIO;

      displayWidthPx = Math.min(
        displayWidthPx,
        Math.max(1, firstPageFitPx) / aspectRatio,
      );
    }

    const fullDisplayHeightPx = displayWidthPx * aspectRatio;

    // The first page of each section also carries the section title, so the
    // first slice must be shorter by the title's height — otherwise the whole
    // (full-page-height) image no longer fits beside the title and Word bumps
    // it to a fresh page, leaving the title alone on an otherwise empty page.
    // Continuation pages have no title and use the full page height, so they
    // stay filled with no empty band at the bottom.
    const headingAllowancePx =
      topAllowanceTwips * pxPerTwip * CreditReportExportService.PAGE_FILL_RATIO;
    let firstPageHeightPx = Math.max(1, availableHeightPx - headingAllowancePx);
    const pxToTwips = (px: number) =>
      Math.round(
        px / pxPerTwip / CreditReportExportService.PAGE_FILL_RATIO,
      );

    const leadingParagraphs: Paragraph[] = [];
    let firstPageUsedPx = headingAllowancePx;

    // Too little room left on the current page (e.g. below a previous
    // table) for the whole table or a useful first slice — start it on a
    // fresh page instead.
    if (
      fullDisplayHeightPx > firstPageHeightPx &&
      firstPageHeightPx <
        availableHeightPx * CreditReportExportService.MIN_FIRST_SLICE_RATIO
    ) {
      leadingParagraphs.push(new Paragraph({ children: [new PageBreak()] }));
      firstPageHeightPx = availableHeightPx;
      firstPageUsedPx = 0;
    }

    // Single image — fits on the first page beside the title.
    if (fullDisplayHeightPx <= firstPageHeightPx) {
      const paragraphs = [
        ...leadingParagraphs,
        new Paragraph({
          alignment: fitOnePage ? AlignmentType.CENTER : undefined,
          spacing: { after: 0 },
          children: [
            new ImageRun({
              type: 'png',
              data: this.dataUrlToUint8Array(image.dataUrl),
              transformation: {
                width: Math.round(displayWidthPx),
                height: Math.round(fullDisplayHeightPx),
              },
            }),
          ],
        }),
      ];

      return {
        paragraphs,
        startsOnNewPage: leadingParagraphs.length > 0,
        lastPageUsedTwips: pxToTwips(firstPageUsedPx + fullDisplayHeightPx),
      };
    }

    // Taller than one page — slice the source canvas into page-height strips:
    // the first strip fits under the title, later strips fill the full page.
    const source = await this.loadImageElement(image.dataUrl);
    const displayPerSourcePx = fullDisplayHeightPx / image.height;
    const firstSliceSourcePx = Math.max(
      1,
      Math.floor(firstPageHeightPx / displayPerSourcePx),
    );
    const fullSliceSourcePx = Math.max(
      1,
      Math.floor(availableHeightPx / displayPerSourcePx),
    );

    // Header strip repeated on every continuation page. Ignored when it would
    // take up more than half a page (nothing useful would fit under it).
    const rawHeaderPx = Math.round(image.headerHeight ?? 0);
    const headerPx =
      rawHeaderPx > 0 && rawHeaderPx * 2 < fullSliceSourcePx ? rawHeaderPx : 0;

    const rowBreaks = (image.rowBreaks ?? [])
      .map((value) => Math.round(value))
      .filter((value) => value > 0 && value < image.height)
      .sort((first, second) => first - second);

    const paragraphs: Paragraph[] = [...leadingParagraphs];
    let offsetY = 0;
    let isFirstSlice = true;
    let lastSliceDisplayPx = 0;

    while (offsetY < image.height) {
      const repeatHeaderPx = isFirstSlice ? 0 : headerPx;
      const sliceCapPx =
        (isFirstSlice ? firstSliceSourcePx : fullSliceSourcePx) - repeatHeaderPx;
      let sliceHeightPx = Math.min(sliceCapPx, image.height - offsetY);

      // End the slice on the last row boundary that fits, so a row is never
      // split across two pages.
      if (offsetY + sliceHeightPx < image.height) {
        const lastFittingBreak = rowBreaks
          .filter((value) => value > offsetY && value <= offsetY + sliceHeightPx)
          .pop();

        if (lastFittingBreak !== undefined) {
          sliceHeightPx = lastFittingBreak - offsetY;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = image.width;
      canvas.height = repeatHeaderPx + sliceHeightPx;

      const context = canvas.getContext('2d');
      if (!context) {
        break;
      }

      if (repeatHeaderPx > 0) {
        context.drawImage(
          source,
          0,
          0,
          image.width,
          repeatHeaderPx,
          0,
          0,
          image.width,
          repeatHeaderPx,
        );
      }

      context.drawImage(
        source,
        0,
        offsetY,
        image.width,
        sliceHeightPx,
        0,
        repeatHeaderPx,
        image.width,
        sliceHeightPx,
      );

      const sliceDataUrl = canvas.toDataURL('image/png');
      offsetY += sliceHeightPx;
      const isLastSlice = offsetY >= image.height;
      lastSliceDisplayPx = canvas.height * displayPerSourcePx;

      paragraphs.push(
        new Paragraph({
          spacing: { after: 0 },
          children: [
            new ImageRun({
              type: 'png',
              data: this.dataUrlToUint8Array(sliceDataUrl),
              transformation: {
                width: Math.round(displayWidthPx),
                height: Math.round(canvas.height * displayPerSourcePx),
              },
            }),
          ],
        }),
      );

      if (!isLastSlice) {
        paragraphs.push(
          new Paragraph({ children: [new PageBreak()] }),
        );
      }

      isFirstSlice = false;
    }

    return {
      paragraphs,
      // The last slice sits on a continuation page, below the line left by
      // the page-break paragraph.
      startsOnNewPage: leadingParagraphs.length > 0,
      lastPageUsedTwips:
        pxToTwips(lastSliceDisplayPx) + convertInchesToTwip(0.3),
    };
  }

  private dataUrlToUint8Array(dataUrl: string): Uint8Array {
    const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index++) {
      bytes[index] = binary.charCodeAt(index);
    }

    return bytes;
  }

  private loadImageElement(dataUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () =>
        reject(new Error('Failed to decode captured table image.'));
      image.src = dataUrl;
    });
  }

  private buildSectionBody(section: ReportSection): Array<Paragraph | Table> {
    // Explicit refValSeq mapping — these three report-only sections carry a
    // section-specific jsonData shape that the generic dispatch below
    // (nested blocks / recommendation / rating / regular blkTyp) cannot
    // recognize, so they must be intercepted first.
    if (Number(section.refValSeq) === this.FINANCIAL_ANALYSIS_DETAILS_REF_VAL_SEQ) {
      return this.buildFinancialAnalysisDetails(section);
    }

    if (Number(section.refValSeq) === this.REPAYMENT_PLAN_REF_VAL_SEQ) {
      return this.buildRepaymentPlan(section);
    }

    if (Number(section.refValSeq) === this.DISBURSEMENT_REPAYMENT_SUMMARY_REF_VAL_SEQ) {
      return this.buildDisbursementRepaymentSummary(section);
    }

    const nestedBlocks = this.getNestedReportBlocks(section);

    if (nestedBlocks.length > 0) {
      return this.buildNestedBlocks(section, nestedBlocks);
    }

    const recommendationRows = this.getRecommendationRows(section);
    if (recommendationRows.length > 0) {
      return [this.buildRecommendationTable(recommendationRows)];
    }

    const ratingData = this.getInstitutionalRatingData(section);
    if (ratingData) {
      return this.buildInstitutionalRating(ratingData);
    }

    return this.buildRegularBlocks(section.blocks);
  }

  /* ====================================================== */
  /* REF-VAL-SEQ MAPPED SECTIONS (Word export)               */
  /* Financial Analysis Details (2647), Repayment Plan       */
  /* (2646), Disbursement & Repayment Summary (2645)         */
  /* Mirrors the on-screen tables in GenrateCreditReport.    */
  /* ====================================================== */

  private buildFinancialAnalysisDetails(
    section: ReportSection,
  ): Array<Paragraph | Table> {
    const data = this.getFinancialAnalysisDetailsData(section);

    if (!data || data.rows.length === 0) {
      return [
        this.compactBodyParagraph(
          'No Financial Analysis Details data available.',
        ),
      ];
    }

    // Laid out like the client's reference Institutional Highlights table:
    // one "Indicators" column (the row's description, or its head when it
    // has none), the periods grouped under their statement type
    // ("Unaudited" / "Audited Accounts") with the year beneath, and CAGR last.
    const size = FIN_HIGHLIGHTS_TEXT_SIZE;
    const periodColumns = this.orderHighlightsColumns(data.periodColumns);
    const periods = periodColumns.map((column) =>
      this.splitHighlightsPeriod(column),
    );
    const showCagr = periods.length > 1;
    const grouped = periods.length > 0 && periods.every((period) => !!period.group);

    // Fixed twips widths (FIXED layout) sized to the executive column it sits
    // in, so Word can't squeeze a column: each period fits a full-PKR amount
    // ("5,001,436,872") on one line, CAGR fits "-10%", and Indicators takes
    // the rest. Many periods shrink evenly so the table never overflows.
    // The executive cell has 120 + 40 twips of side margins; a further 100
    // keeps the right border (drawn half outside the table edge) inside the
    // cell, which clips anything wider.
    const tableWidth = this.executiveColumnWidthTwips - 160 - 100;
    // CAGR fits a large figure like "12203%" on one line; the extra width
    // comes out of Indicators, the period columns keep their size.
    const cagrWidth = showCagr ? 1000 : 0;
    const periodWidth =
      periods.length > 0
        ? Math.min(1250, Math.floor((tableWidth * 0.62 - (showCagr ? 700 : 0)) / periods.length))
        : 0;
    const indicatorWidth = tableWidth - cagrWidth - periodWidth * periods.length;

    const headerRows: TableRow[] = [];

    if (grouped) {
      const groups: { label: string; span: number }[] = [];

      periods.forEach((period) => {
        const last = groups[groups.length - 1];

        if (last && last.label === period.group) {
          last.span++;
        } else {
          groups.push({ label: period.group, span: 1 });
        }
      });

      headerRows.push(
        new TableRow({
          tableHeader: true,
          children: [
            this.headerCell('Indicators', { rowSpan: 2, widthTwips: indicatorWidth, size }),
            ...groups.map((group) =>
              this.headerCell(group.label, {
                columnSpan: group.span,
                widthTwips: periodWidth * group.span,
                size,
              }),
            ),
            ...(showCagr
              ? [this.headerCell('CAGR', { rowSpan: 2, widthTwips: cagrWidth, size })]
              : []),
          ],
        }),
        new TableRow({
          tableHeader: true,
          children: periods.map((period) =>
            this.headerCell(period.label, { widthTwips: periodWidth, size }),
          ),
        }),
      );
    } else {
      headerRows.push(
        new TableRow({
          tableHeader: true,
          children: [
            this.headerCell('Indicators', { widthTwips: indicatorWidth, size }),
            ...periods.map((period) =>
              this.headerCell(period.label, { widthTwips: periodWidth, size }),
            ),
            ...(showCagr
              ? [this.headerCell('CAGR', { widthTwips: cagrWidth, size })]
              : []),
          ],
        }),
      );
    }

    const bodyRows = data.rows.map((row) => {
      const dscr = row.dscr && row.dscr !== '-' ? row.dscr : '';

      return new TableRow({
        children: [
          this.bodyCell(dscr || row.head || '-', {
            size,
            widthTwips: indicatorWidth,
          }),
          ...periodColumns.map((period) => {
            const value = row.periodValues?.[period];

            return this.bodyCell(
              value !== null && value !== undefined
                ? this.formatAmount(value)
                : '-',
              { right: true, size, widthTwips: periodWidth },
            );
          }),
          ...(showCagr
            ? [
                this.bodyCell(this.highlightsCagr(row, periodColumns, periods), {
                  right: true,
                  size,
                  widthTwips: cagrWidth,
                }),
              ]
            : []),
        ],
      });
    });

    return [
      new Table({
        width: { size: tableWidth, type: WidthType.DXA },
        layout: TableLayoutType.FIXED,
        columnWidths: [
          indicatorWidth,
          ...periods.map(() => periodWidth),
          ...(showCagr ? [cagrWidth] : []),
        ],
        rows: [...headerRows, ...bodyRows],
        borders: this.tableBorders(),
      }),
    ];
  }

  /**
   * Highlights columns in the reference's order: each statement type kept
   * together under one header (the type holding the newest year first),
   * newest year first within it — e.g. Unaudited 2026 | Audited 2025, 2024.
   * Columns without a year keep their original relative order.
   */
  orderHighlightsColumns(columns: string[]): string[] {
    const entries = columns.map((column, index) => ({
      column,
      index,
      ...this.splitHighlightsPeriod(column),
    }));

    const newestByGroup = new Map<string, number>();

    entries.forEach((entry) => {
      const year = entry.year ?? -Infinity;
      newestByGroup.set(entry.group, Math.max(newestByGroup.get(entry.group) ?? -Infinity, year));
    });

    const groupOrder = [...newestByGroup.entries()]
      .sort((first, second) => second[1] - first[1])
      .map(([group]) => group);

    return [...entries]
      .sort(
        (first, second) =>
          groupOrder.indexOf(first.group) - groupOrder.indexOf(second.group) ||
          (second.year ?? -Infinity) - (first.year ?? -Infinity) ||
          first.index - second.index,
      )
      .map((entry) => entry.column);
  }

  /**
   * "2025 (Audited)" → year label "2025" under the group "Audited Accounts"
   * (the reference's wording); "2024 (Unaudited)" → "2024" under "Unaudited".
   * A label without a bracketed type has no group.
   */
  splitHighlightsPeriod(
    column: string,
  ): { label: string; group: string; year: number | null } {
    const match = /^(.*?)\s*\(([^)]*)\)\s*$/.exec(column);
    const label = (match ? match[1] : column).trim() || column;
    const type = match ? match[2].trim() : '';
    const yearMatch = /\b(\d{4})\b/.exec(label);

    return {
      label,
      group: /^audited$/i.test(type) ? 'Audited Accounts' : type,
      year: yearMatch ? Number(yearMatch[1]) : null,
    };
  }

  /**
   * CAGR from the oldest to the newest period of the row, rounded to a whole
   * percent like the reference ("11%"). "-" only when no two periods can be
   * compared (no positive start value, a negative newest value, or no years
   * between them).
   */
  highlightsCagr(
    row: FinancialAnalysisReportRow,
    columns: string[],
    periods: { year: number | null }[],
  ): string {
    const points = columns
      .map((column, index) => ({
        value: row.periodValues?.[column],
        // Without a year, column order stands in for time (one step each).
        time: periods[index]?.year ?? index,
      }))
      .filter(
        (point): point is { value: number; time: number } =>
          point.value !== null && point.value !== undefined,
      )
      .sort((first, second) => first.time - second.time);

    // Growth can't be measured from 0, so the start is the oldest period
    // with a positive value (a 0 in the first year moves the start to the
    // next year). A 0 in the newest year is a real fall and gives -100%.
    const startIndex = points.findIndex((point) => point.value > 0);
    const oldest = startIndex >= 0 ? points[startIndex] : undefined;
    const newest = points[points.length - 1];

    if (!oldest || startIndex === points.length - 1) {
      return '-';
    }

    const years = newest.time - oldest.time;

    if (years <= 0 || newest.value < 0) {
      return '-';
    }

    const cagr = Math.pow(newest.value / oldest.value, 1 / years) - 1;

    return Number.isFinite(cagr) ? `${Math.round(cagr * 100)}%` : '-';
  }

  /**
   * Financial Analysis — separate trailing report section (mirrors the
   * on-screen single-column layout in GenrateCreditReport: each row's
   * `head` renders as its own bold heading row, `dscr` rows sit indented
   * beneath it, and "Total ..." rows render bold/unindented as subtotals).
   */
  private buildFinancialAnalysisSummarySection(
    rows: FinancialAnalysisReportRow[],
    columns: string[],
    sectionNumber: number,
    label?: string,
  ): Array<Paragraph | Table> {
    const output: Array<Paragraph | Table> = [
      this.sectionTitle('Financial Analysis', sectionNumber, label),
    ];

    if (rows.length === 0) {
      output.push(
        this.compactBodyParagraph(
          'No Financial Analysis data available.',
        ),
      );

      return output;
    }

    const tableRows: TableRow[] = [
      new TableRow({
        tableHeader: true,
        children: [
          this.headerCell(''),
          ...columns.map((period) => this.headerCell(period)),
        ],
      }),
    ];

    let previousHeadKey: string | null = null;

    rows.forEach((row) => {
      const headKey = this.normalizeForComparison(row.head);
      const isSubtotal = this.normalizeForComparison(
        row.dscr,
      ).startsWith('total');

      if (row.head && headKey !== previousHeadKey) {
        tableRows.push(
          new TableRow({
            children: [
              this.bodyCell(row.head, {
                bold: true,
                columnSpan: columns.length + 1,
              }),
            ],
          }),
        );
      }

      previousHeadKey = headKey;

      const cells: TableCell[] = [
        this.bodyCell(
          isSubtotal
            ? row.dscr || '-'
            : `   ${row.dscr || '-'}`,
          { bold: isSubtotal },
        ),
      ];

      columns.forEach((period) => {
        const value = row.periodValues?.[period];

        cells.push(
          this.bodyCell(
            value !== null && value !== undefined
              ? this.formatAmount(value)
              : '-',
            { center: true, bold: isSubtotal },
          ),
        );
      });

      tableRows.push(new TableRow({ children: cells }));
    });

    output.push(
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: tableRows,
        borders: this.tableBorders(),
      }),
    );

    const imageParagraphs = this.capturedTableImageParagraphs.get(
      'financial-analysis-summary',
    );

    // Native fallback: same annexure label as the captured image, above the table.
    return imageParagraphs
      ? this.replaceTablesWithImage(output, imageParagraphs)
      : [
          ...output.slice(0, -1),
          ...this.buildAnnexureHeading('financial-analysis-summary'),
          ...output.slice(-1),
        ];
  }

  private buildRepaymentPlan(
    section: ReportSection,
  ): Array<Paragraph | Table> {
    const data = this.repaymentPlanData ?? this.getRepaymentPlanData(section);

    if (!data || data.rows.length === 0) {
      return [
        this.compactBodyParagraph('No Repayment Plan data available.'),
      ];
    }

    // One schedule per repayment term type (Conventional / Islamic), then
    // the consolidated table (the Type column carries the term type) under
    // its own "Consolidated Tentative Repayment Plan" heading. Tables only,
    // plus the summary line: when the captured images replace the tables the
    // heading is added in prepareCapturedTableImageParagraphs instead.
    const output: Array<Paragraph | Table> = [];

    splitRepaymentPlanByType(data).forEach((group, index) => {
      if (index > 0) {
        // Keeps the tables apart (adjacent docx tables merge into one).
        output.push(new Paragraph({ spacing: { after: 240 }, children: [] }));
      }

      output.push(
        this.buildRepaymentPlanTypeTable(
          group.data,
          group.type,
          group.showOpeningExposure,
        ),
      );
    });

    output.push(new Paragraph({ spacing: { after: 240 }, children: [] }));
    output.push(this.buildRepaymentPlanTable(data));

    output.push(
      this.compactBodyParagraph(
        `Total Disbursement: ${this.formatAmount(data.totalDisbursementAmount)} · Total Repayment: ${this.formatAmount(data.grandTotal)} (PKR)`,
      ),
    );

    return output;
  }

  /** One repayment term type's Tentative Repayment Plan table. */
  private buildRepaymentPlanTypeTable(
    data: RepaymentPlanReportData,
    typeLabel: string,
    showOpeningExposure: boolean,
  ): Table {
    const totalColumnCount = data.columns.length + 4;
    const columnWidths = this.buildRepaymentPlanColumnWidths(
      data.columns.length,
      false,
    );

    const repaymentHeaderCell = (
      text: string,
      cellOptions: { columnSpan?: number; rowSpan?: number } = {},
    ) =>
      this.headerCell(text, {
        ...cellOptions,
        size: REPAYMENT_TABLE_HEADER_TEXT_SIZE,
      });

    const repaymentBodyCell = (
      text: string,
      cellOptions: {
        bold?: boolean;
        center?: boolean;
        rowSpan?: number;
        columnSpan?: number;
      } = {},
    ) =>
      this.bodyCell(text, {
        ...cellOptions,
        size: REPAYMENT_TABLE_TEXT_SIZE,
      });

    const rows: TableRow[] = [
      // Repayment term type (e.g. Conventional / Islamic) as the table heading.
      ...(typeLabel
        ? [
            new TableRow({
              tableHeader: true,
              children: [
                repaymentHeaderCell(typeLabel, {
                  columnSpan: totalColumnCount,
                }),
              ],
            }),
          ]
        : []),

      new TableRow({
        tableHeader: true,
        children: [
          repaymentHeaderCell('Disbursements', { columnSpan: 3 }),
          repaymentHeaderCell('Repayment Schedule', {
            columnSpan: data.columns.length + 1,
          }),
        ],
      }),

      new TableRow({
        tableHeader: true,
        children: [
          repaymentHeaderCell('Quarter'),
          repaymentHeaderCell('Month'),
          repaymentHeaderCell('Tranche Amount'),
          ...data.columns.map((column) =>
            repaymentHeaderCell(this.formatRepaymentPeriod(column)),
          ),
          repaymentHeaderCell('Total'),
        ],
      }),
    ];

    if (showOpeningExposure) {
      rows.push(
        new TableRow({
          children: [
            repaymentBodyCell('Opening Exposure', {
              bold: true,
              columnSpan: 2,
            }),
            repaymentBodyCell(this.formatAmount(data.openingExposure), {
              center: true,
              bold: true,
            }),
            ...data.columns.map(() => repaymentBodyCell('')),
            repaymentBodyCell(''),
          ],
        }),
      );
    }

    data.rows.forEach((row) => {
      rows.push(
        new TableRow({
          children: [
            repaymentBodyCell(row.quarter || '-'),
            repaymentBodyCell(this.formatRepaymentPeriod(row.month)),
            repaymentBodyCell(this.formatAmount(row.trancheAmount), {
              center: true,
            }),
            ...data.columns.map((column) =>
              repaymentBodyCell(
                Object.prototype.hasOwnProperty.call(row.schedule, column)
                  ? this.formatAmount(row.schedule[column])
                  : '-',
                { center: true },
              ),
            ),
            repaymentBodyCell(
              this.formatAmount(this.getRepaymentRowTotal(row)),
              { center: true, bold: true },
            ),
          ],
        }),
      );
    });

    rows.push(
      new TableRow({
        children: [
          repaymentBodyCell('Total', { bold: true, columnSpan: 3 }),
          ...data.columns.map((column) =>
            repaymentBodyCell(
              this.formatAmount(
                this.getRepaymentColumnTotal(data, column),
              ),
              { center: true, bold: true },
            ),
          ),
          repaymentBodyCell(this.formatAmount(data.grandTotal), {
            center: true,
            bold: true,
          }),
        ],
      }),
    );

    return new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      columnWidths,
      layout: TableLayoutType.FIXED,
      rows,
      borders: this.tableBorders(),
    });
  }

  /** The consolidated Tentative Repayment Plan table (all repayment term types). */
  private buildRepaymentPlanTable(data: RepaymentPlanReportData): Table {
    const columnWidths = this.buildRepaymentPlanColumnWidths(
      data.columns.length,
    );

    const repaymentHeaderCell = (
      text: string,
      cellOptions: { columnSpan?: number; rowSpan?: number } = {},
    ) =>
      this.headerCell(text, {
        ...cellOptions,
        size: REPAYMENT_TABLE_HEADER_TEXT_SIZE,
      });

    const repaymentBodyCell = (
      text: string,
      cellOptions: {
        bold?: boolean;
        center?: boolean;
        rowSpan?: number;
        columnSpan?: number;
      } = {},
    ) =>
      this.bodyCell(text, {
        ...cellOptions,
        size: REPAYMENT_TABLE_TEXT_SIZE,
      });

    const rows: TableRow[] = [
      new TableRow({
        tableHeader: true,
        children: [
          this.captionCell(
            CONSOLIDATED_REPAYMENT_PLAN_TITLE,
            data.columns.length + 5,
          ),
        ],
      }),

      new TableRow({
        tableHeader: true,
        children: [
          repaymentHeaderCell('Disbursements', { columnSpan: 4 }),
          repaymentHeaderCell('Repayment Schedule', {
            columnSpan: data.columns.length + 1,
          }),
        ],
      }),

      new TableRow({
        tableHeader: true,
        children: [
          repaymentHeaderCell('Quarter'),
          repaymentHeaderCell('Type'),
          repaymentHeaderCell('Month'),
          repaymentHeaderCell('Tranche Amount'),
          ...data.columns.map((column) =>
            repaymentHeaderCell(this.formatRepaymentPeriod(column)),
          ),
          repaymentHeaderCell('Total'),
        ],
      }),

      new TableRow({
        children: [
          repaymentBodyCell('Opening Exposure', {
            bold: true,
            columnSpan: 3,
          }),
          repaymentBodyCell(this.formatAmount(data.openingExposure), {
            center: true,
            bold: true,
          }),
          ...data.columns.map(() => repaymentBodyCell('')),
          repaymentBodyCell(''),
        ],
      }),
    ];

    data.rows.forEach((row) => {
      rows.push(
        new TableRow({
          children: [
            repaymentBodyCell(row.quarter || '-'),
            repaymentBodyCell(row.type || '-'),
            repaymentBodyCell(this.formatRepaymentPeriod(row.month)),
            repaymentBodyCell(this.formatAmount(row.trancheAmount), {
              center: true,
            }),
            ...data.columns.map((column) =>
              repaymentBodyCell(
                Object.prototype.hasOwnProperty.call(row.schedule, column)
                  ? this.formatAmount(row.schedule[column])
                  : '-',
                { center: true },
              ),
            ),
            repaymentBodyCell(
              this.formatAmount(this.getRepaymentRowTotal(row)),
              { center: true, bold: true },
            ),
          ],
        }),
      );
    });

    rows.push(
      new TableRow({
        children: [
          repaymentBodyCell('Total', { bold: true, columnSpan: 4 }),
          ...data.columns.map((column) =>
            repaymentBodyCell(
              this.formatAmount(
                this.getRepaymentColumnTotal(data, column),
              ),
              { center: true, bold: true },
            ),
          ),
          repaymentBodyCell(this.formatAmount(data.grandTotal), {
            center: true,
            bold: true,
          }),
        ],
      }),
    );

    return new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      columnWidths,
      layout: TableLayoutType.FIXED,
      rows,
      borders: this.tableBorders(),
    });
  }

  /**
   * Fixed widths for Quarter/Type/Month/Tranche/Total, with whatever landscape
   * width remains split evenly across the period columns (floored so a
   * long period list still gets a readable minimum instead of shrinking
   * into per-letter wrapping).
   */
  private buildRepaymentPlanColumnWidths(
    periodColumnCount: number,
    includeType = true,
  ): number[] {
    const quarterWidth = 1200;
    const typeWidth = includeType ? 1100 : 0;
    const monthWidth = 700;
    // Full-PKR amounts (e.g. 125,000,000) need more room than millions did.
    const trancheWidth = 1250;
    const totalWidth = 1250;
    const minPeriodWidth = 1000;

    const fixedWidth =
      quarterWidth + typeWidth + monthWidth + trancheWidth + totalWidth;

    const availableForPeriods = Math.max(
      this.landscapeContentWidthTwips - fixedWidth,
      minPeriodWidth * Math.max(periodColumnCount, 1),
    );

    const periodWidth = Math.max(
      minPeriodWidth,
      Math.floor(
        availableForPeriods / Math.max(periodColumnCount, 1),
      ),
    );

    return [
      quarterWidth,
      ...(includeType ? [typeWidth] : []),
      monthWidth,
      trancheWidth,
      ...Array(periodColumnCount).fill(periodWidth),
      totalWidth,
    ];
  }

  /** Full-width title bar — white background, black text, no fill of any kind. */
  private captionCell(text: string, columnSpan: number): TableCell {
    return new TableCell({
      columnSpan,
      verticalAlign: VerticalAlign.CENTER,
      margins: this.cellMargins(),
      children: [
        new Paragraph({
          alignment: AlignmentType.CENTER,
          contextualSpacing: false,
          spacing: {
            before: 0,
            after: TABLE_SPACING_AFTER,
            line: TABLE_HEADER_LINE_SPACING,
            lineRule: LineRuleType.AUTO,
          },
          children: [
            new TextRun({
              text,
              bold: true,
              font: FONT,
              color: BLACK,
              size: REPAYMENT_TABLE_HEADER_TEXT_SIZE,
            }),
          ],
        }),
      ],
    });
  }

  private buildDisbursementRepaymentSummary(
    section: ReportSection,
  ): Array<Paragraph | Table> {
    const data = this.getDisbursementRepaymentSummaryData(section);

    if (!data || data.rows.length === 0) {
      // The on-screen tables (one per repayment term) come from the live plan
      // API; when they were captured, the snapshots alone fill this section.
      if (this.capturedTableImageParagraphs.has('disbursement-summary')) {
        return [];
      }

      return [
        this.compactBodyParagraph(
          'No Disbursement and Repayment Summary data available.',
        ),
      ];
    }

    const rows: TableRow[] = [
      new TableRow({
        tableHeader: true,
        children: [
          this.headerCell('Month'),
          this.headerCell('Proposed Disbursements'),
          this.headerCell('Proposed Repayments'),
          this.headerCell('Scheduled Disbursements'),
          this.headerCell('Scheduled Repayments'),
          this.headerCell('Maximum Exposure'),
        ],
      }),

      new TableRow({
        children: [
          this.bodyCell('Opening', { bold: true }),
          this.bodyCell(''),
          this.bodyCell(''),
          this.bodyCell(''),
          this.bodyCell(''),
          this.bodyCell(this.formatAmount(data.openingExposure), {
            center: true,
            bold: true,
          }),
        ],
      }),
    ];

    data.rows.forEach((row) => {
      rows.push(
        new TableRow({
          children: [
            this.bodyCell(this.formatSummaryMonth(row.month), { bold: true }),
            this.bodyCell(
              row.proposedDisbursement > 0
                ? this.formatAmount(row.proposedDisbursement)
                : '-',
              { center: true },
            ),
            this.bodyCell(
              row.proposedRepayment > 0
                ? this.formatAmount(row.proposedRepayment)
                : '-',
              { center: true },
            ),
            this.bodyCell(
              row.scheduledDisbursement > 0
                ? this.formatAmount(row.scheduledDisbursement)
                : '-',
              { center: true },
            ),
            this.bodyCell(
              row.scheduledRepayment > 0
                ? this.formatAmount(row.scheduledRepayment)
                : '-',
              { center: true },
            ),
            this.bodyCell(this.formatAmount(row.maximumExposure), {
              center: true,
              bold: true,
            }),
          ],
        }),
      );
    });

    rows.push(
      new TableRow({
        children: [
          this.bodyCell('Total', { bold: true }),
          this.bodyCell(this.formatAmount(data.totals.proposedDisbursement), {
            center: true,
            bold: true,
          }),
          this.bodyCell(this.formatAmount(data.totals.proposedRepayment), {
            center: true,
            bold: true,
          }),
          this.bodyCell(
            this.formatAmount(data.totals.scheduledDisbursement),
            { center: true, bold: true },
          ),
          this.bodyCell(this.formatAmount(data.totals.scheduledRepayment), {
            center: true,
            bold: true,
          }),
          this.bodyCell(''),
        ],
      }),
    );

    return [
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows,
        borders: this.tableBorders(),
      }),
    ];
  }

  /* ---------- data extraction (mirrors GenrateCreditReport) ---------- */

  private getFinancialAnalysisDetailsData(
    section: ReportSection,
  ): FinancialAnalysisReportData | null {
    const latestBlock = this.getLatestBlock(section);

    if (!latestBlock) {
      return null;
    }

    const rawItem = this.findRawJsonItem(
      latestBlock,
      (candidate) =>
        candidate['type'] === 'financial-analysis-details' ||
        Array.isArray(candidate['rows']),
    );

    if (!rawItem) {
      return null;
    }

    const rawRows = Array.isArray(rawItem['rows'])
      ? (rawItem['rows'] as unknown[])
      : [];

    const rows: FinancialAnalysisReportRow[] = rawRows
      .filter(
        (row): row is Record<string, unknown> =>
          !!row && typeof row === 'object',
      )
      .map((row) => ({
        institution: String(row['institution'] ?? '').trim(),
        sortOrder: this.toNumber(row['sortOrder']),
        fncTypSeq: this.toNumber(row['fncTypSeq']),
        fncValSeq: this.toNumber(row['fncValSeq']),
        head: String(row['head'] ?? '').trim(),
        dscr: String(row['dscr'] ?? '').trim(),
        periodValues: this.normalizeNullableAmountRecord(
          row['periodValues'],
        ),
      }))
      .sort(
        (first, second) =>
          first.sortOrder - second.sortOrder ||
          first.fncTypSeq - second.fncTypSeq ||
          first.fncValSeq - second.fncValSeq,
      );

    const rawPeriodColumns = Array.isArray(rawItem['periodColumns'])
      ? (rawItem['periodColumns'] as unknown[])
          .map((column) => String(column ?? '').trim())
          .filter(Boolean)
      : [];

    // Only periods that carry a value in at least one row — a year/statement
    // type with no saved figures (e.g. the Unaudited column of a year saved
    // as Audited) is not printed as an empty column.
    const periodColumns = (
      rawPeriodColumns.length > 0
        ? rawPeriodColumns
        : this.derivePeriodColumns(rows.map((row) => row.periodValues))
    ).filter((period) =>
      rows.some(
        (row) =>
          row.periodValues[period] !== null &&
          row.periodValues[period] !== undefined,
      ),
    );

    const institutionName =
      String(rawItem['institutionName'] ?? '').trim() ||
      rows.find((row) => row.institution)?.institution ||
      '';

    return { institutionName, periodColumns, rows };
  }

  private getRepaymentPlanData(
    section: ReportSection,
  ): RepaymentPlanReportData | null {
    const latestBlock = this.getLatestBlock(section);

    if (!latestBlock || !Array.isArray(latestBlock.jsonData)) {
      return null;
    }

    const rows: RepaymentPlanReportRow[] = flattenRepaymentTrancheRows(
      latestBlock.jsonData as unknown[],
    )
      .map((item) => {
        const month = String(item['month'] ?? '').slice(0, 10);
        const rawQuarter = String(item['quarter'] ?? '').trim();

        return {
          prpsdDsbmntSeq: this.toNumber(item['prpsdDsbmntSeq']),
          // Not returned by the backend for every proposal — derive the
          // calendar-quarter label from the tranche month instead of
          // leaving it blank, unless the backend did supply one.
          quarter: rawQuarter || this.getQuarterLabel(month),
          // No picklist access here — the caller passes `repaymentPlanData`
          // with resolved term labels; this block fallback leaves it blank.
          type: '',
          month,
          trancheAmount: this.toNumber(item['prpsdDsbmntAmount']),
          schedule: this.normalizeAmountRecord(
            item['installmentsByPeriod'],
          ),
        };
      })
      .sort((first, second) => first.month.localeCompare(second.month));

    const columns = this.derivePeriodColumns(
      rows.map((row) => row.schedule),
    );

    const totalDisbursementAmount = this.roundAmount(
      rows.reduce((total, row) => total + row.trancheAmount, 0),
    );

    const grandTotal = this.roundAmount(
      rows.reduce(
        (total, row) => total + this.getRepaymentRowTotal(row),
        0,
      ),
    );

    return {
      columns,
      rows,
      totalDisbursementAmount,
      grandTotal,
      openingExposure: this.openingExposure,
    };
  }

  private getDisbursementRepaymentSummaryData(
    section: ReportSection,
  ): DisbursementRepaymentSummaryReportData | null {
    const latestBlock = this.getLatestBlock(section);

    if (!latestBlock || !Array.isArray(latestBlock.jsonData)) {
      return null;
    }

    const rows: DisbursementRepaymentSummaryReportRow[] = (
      latestBlock.jsonData as unknown[]
    )
      .filter(
        (item): item is Record<string, unknown> =>
          !!item && typeof item === 'object',
      )
      .map((item) => ({
        month: String(item['schMth'] ?? '').slice(0, 10),
        proposedDisbursement: this.toNumber(item['prpsdDsbmtAmt']),
        proposedRepayment: this.toNumber(item['prpsdRpmtAmt']),
        scheduledDisbursement: this.toNumber(item['schDsbmtAmt']),
        scheduledRepayment: this.toNumber(item['schRpmtAmt']),
        maximumExposure: this.toNumber(item['maxExpsrAmt']),
      }))
      .sort((first, second) => first.month.localeCompare(second.month));

    const totals = {
      proposedDisbursement: this.roundAmount(
        rows.reduce((total, row) => total + row.proposedDisbursement, 0),
      ),
      proposedRepayment: this.roundAmount(
        rows.reduce((total, row) => total + row.proposedRepayment, 0),
      ),
      scheduledDisbursement: this.roundAmount(
        rows.reduce((total, row) => total + row.scheduledDisbursement, 0),
      ),
      scheduledRepayment: this.roundAmount(
        rows.reduce((total, row) => total + row.scheduledRepayment, 0),
      ),
    };

    return {
      rows,
      openingExposure: this.openingExposure,
      totals,
    };
  }

  private getRepaymentRowTotal(row: RepaymentPlanReportRow): number {
    return this.roundAmount(
      Object.values(row.schedule).reduce(
        (total, value) => total + this.toNumber(value),
        0,
      ),
    );
  }

  private getRepaymentColumnTotal(
    data: RepaymentPlanReportData,
    period: string,
  ): number {
    return this.roundAmount(
      data.rows.reduce(
        (total, row) => total + this.toNumber(row.schedule[period]),
        0,
      ),
    );
  }

  private formatRepaymentPeriod(period: string): string {
    const parts = String(period).slice(0, 10).split('-');

    if (parts.length < 2) {
      return period;
    }

    const year = Number(parts[0]);
    const month = Number(parts[1]);

    if (!Number.isFinite(year) || !Number.isFinite(month)) {
      return period;
    }

    return new Intl.DateTimeFormat('en-GB', {
      month: 'short',
      year: '2-digit',
    }).format(new Date(year, month - 1, 1));
  }

  /** e.g. '2025-01-15' -> 'Jan-Mar 2025' (the calendar quarter containing that month). */
  private getQuarterLabel(month: string): string {
    const parts = String(month ?? '').slice(0, 10).split('-');

    if (parts.length < 2) {
      return '';
    }

    const year = Number(parts[0]);
    const monthNum = Number(parts[1]);

    if (!Number.isFinite(year) || !Number.isFinite(monthNum)) {
      return '';
    }

    const quarterStartMonth = Math.floor((monthNum - 1) / 3) * 3 + 1;

    const startLabel = new Intl.DateTimeFormat('en-US', {
      month: 'short',
    }).format(new Date(year, quarterStartMonth - 1, 1));

    const endLabel = new Intl.DateTimeFormat('en-US', {
      month: 'short',
    }).format(new Date(year, quarterStartMonth + 1, 1));

    return `${startLabel}-${endLabel} ${year}`;
  }

  private formatSummaryMonth(month: string): string {
    const match = /^(\d{4})-(\d{2})-\d{2}$/.exec(
      String(month ?? '').slice(0, 10),
    );

    if (!match) {
      return month || '-';
    }

    const year = Number(match[1]);
    const monthIndex = Number(match[2]) - 1;

    if (!Number.isFinite(year) || !Number.isFinite(monthIndex)) {
      return month;
    }

    return new Intl.DateTimeFormat('en-GB', {
      month: 'short',
      year: '2-digit',
    }).format(new Date(year, monthIndex, 1));
  }

  private formatAmount(value: number): string {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }

  /**
   * These report-only sections can accumulate more than one saved block for
   * the same refValSeq — rendering all of them would duplicate/mix stale
   * data. Picks the single most recent block: highest `crdtPrpslBlkSeq`,
   * falling back to the latest `lastUpdDt` on a tie.
   */
  private getLatestBlock(
    section: ReportSection,
  ): CreditSectionBlock | null {
    if (section.blocks.length === 0) {
      return null;
    }

    return [...section.blocks].sort((first, second) => {
      const blockSeqDiff =
        Number(second.crdtPrpslBlkSeq ?? 0) -
        Number(first.crdtPrpslBlkSeq ?? 0);

      if (blockSeqDiff !== 0) {
        return blockSeqDiff;
      }

      return (
        this.toTimestamp(second.lastUpdDt) -
        this.toTimestamp(first.lastUpdDt)
      );
    })[0];
  }

  private toTimestamp(value: string | null | undefined): number {
    if (!value) {
      return 0;
    }

    const time = new Date(value).getTime();

    return Number.isFinite(time) ? time : 0;
  }

  /**
   * Finds the first jsonData entry in `block` whose raw shape satisfies
   * `matches`, read as `Record<string, unknown>` rather than forced through
   * the generic `SectionJsonData` interface (this section's shape is
   * section-specific).
   */
  private findRawJsonItem(
    block: CreditSectionBlock,
    matches: (candidate: Record<string, unknown>) => boolean,
  ): Record<string, unknown> | null {
    if (!Array.isArray(block.jsonData)) {
      return null;
    }

    for (const item of block.jsonData as unknown[]) {
      if (!item || typeof item !== 'object') {
        continue;
      }

      const candidate = item as Record<string, unknown>;

      if (matches(candidate)) {
        return candidate;
      }
    }

    return null;
  }

  private normalizeForComparison(value: unknown): string {
    return String(value ?? '').trim().toLowerCase();
  }

  private toNumber(value: unknown, fallback = 0): number {
    const numeric = Number(value ?? fallback);
    return Number.isFinite(numeric) ? numeric : fallback;
  }

  private toNumberOrNull(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }

  private roundAmount(value: number): number {
    return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
  }

  private normalizeAmountRecord(
    value: unknown,
  ): Record<string, number> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return {};
    }

    const normalized: Record<string, number> = {};

    Object.entries(value as Record<string, unknown>).forEach(
      ([key, raw]) => {
        normalized[key] = this.toNumber(raw);
      },
    );

    return normalized;
  }

  private normalizeNullableAmountRecord(
    value: unknown,
  ): Record<string, number | null> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return {};
    }

    const normalized: Record<string, number | null> = {};

    Object.entries(value as Record<string, unknown>).forEach(
      ([key, raw]) => {
        normalized[key] = this.toNumberOrNull(raw);
      },
    );

    return normalized;
  }

  private derivePeriodColumns(
    records: Array<
      Record<string, number | null> | Record<string, number>
    >,
  ): string[] {
    const periods = new Set<string>();

    records.forEach((record) => {
      Object.keys(record ?? {}).forEach((period) => periods.add(period));
    });

    return Array.from(periods).sort((first, second) =>
      first.localeCompare(second, undefined, {
        numeric: true,
        sensitivity: 'base',
      }),
    );
  }

  /* ====================================================== */
  /* NESTED REPORT BLOCKS                                  */
  /* ====================================================== */

  private getNestedReportBlocks(section: ReportSection): NestedReportBlock[] {
    return section.blocks
      .flatMap((block) => (Array.isArray(block.jsonData) ? block.jsonData : []))
      .flatMap((item) => (Array.isArray(item.blocks) ? item.blocks : []))
      .filter((block) => Number(block?.delFlg ?? 0) !== 1);
  }

  private buildNestedBlocks(
    section: ReportSection,
    blocks: NestedReportBlock[],
  ): Array<Paragraph | Table> {
    const output: Array<Paragraph | Table> = [];

    blocks.forEach((block) => {
      const showTitle = this.showNestedBlockTitle(section, block);

      switch (block.type) {
        case 'heading':
          if (showTitle) {
            output.push(this.subHeading(block.title ?? ''));
          }
          break;

        case 'paragraph':
          if (showTitle) {
            output.push(this.subHeading(block.title ?? ''));
          }

          if (block.content) {
            output.push(...this.bodyParagraphs(block.content));
          }
          break;

        case 'bullet-list':
          if (showTitle) {
            output.push(this.subHeading(block.title ?? ''));
          }

          output.push(...this.bulletList(this.listLines(block.content)));
          break;

        case 'numbered-list':
          if (showTitle) {
            output.push(this.subHeading(block.title ?? ''));
          }

          output.push(...this.numberedList(this.listLines(block.content)));
          break;

        case 'table':
          if (block.table) {
            if (showTitle) {
              output.push(this.smallHeading(block.title ?? ''));
            }

            output.push(this.buildGenericTable(block.table));

            if (block.content) {
              output.push(...this.captionParagraphs(block.content));
            }
          }
          break;
      }
    });

    return output;
  }

  private showNestedBlockTitle(
    section: ReportSection,
    block: NestedReportBlock,
  ): boolean {
    const sectionTitle = String(section.title ?? '').trim().toLowerCase();
    const blockTitle = String(block.title ?? '').trim().toLowerCase();

    return Boolean(blockTitle && blockTitle !== sectionTitle);
  }

  /* ====================================================== */
  /* GENERIC TABLE                                         */
  /* ====================================================== */

  private buildGenericTable(table: NestedTable): Table {
    const columns = Array.isArray(table?.columns) ? table.columns : [];
    const dataRows = Array.isArray(table?.rows) ? table.rows : [];
    const hasSubColumns = columns.some(
      (column) => (column.subColumns?.length ?? 0) > 0,
    );

    const rows: TableRow[] = [
      new TableRow({
        tableHeader: true,
        children: columns.map((column) => {
          const subColumnCount = column.subColumns?.length ?? 0;

          return this.headerCell(column.title, {
            columnSpan: subColumnCount > 0 ? subColumnCount : undefined,
            rowSpan: hasSubColumns && subColumnCount === 0 ? 2 : undefined,
          });
        }),
      }),
    ];

    if (hasSubColumns) {
      const subHeaderCells: TableCell[] = [];

      columns.forEach((column) => {
        (column.subColumns ?? []).forEach((subColumn) => {
          subHeaderCells.push(this.headerCell(subColumn));
        });
      });

      rows.push(
        new TableRow({
          tableHeader: true,
          children: subHeaderCells,
        }),
      );
    }

    const leafTitles = tableLeafTitles(columns);

    dataRows.forEach((row) => {
      rows.push(
        new TableRow({
          children: row.map((cell, cellIndex) => {
            const formatted = formatTableCell(cell, leafTitles[cellIndex]);

            return this.bodyCell(formatted.text.trim() ? formatted.text : '-', {
              bold: cellIndex === 0,
              center: cellIndex > 0 && !formatted.numeric,
              right: formatted.numeric,
            });
          }),
        }),
      );
    });

    return new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows,
      borders: this.tableBorders(),
    });
  }

  /* ====================================================== */
  /* INSTITUTIONAL RATING                                  */
  /* ====================================================== */

  private getInstitutionalRatingData(section: ReportSection): SectionJsonData | null {
    const isKnownRatingSection =
      section.type === SectionType.IRS_RATING ||
      section.type === SectionType.IRS_RATING_SUMRY ||
      Number(section.refValSeq) === Number(PKL_GRP.INSTITUTIONAL_RATING);

    for (const block of section.blocks) {
      if (!Array.isArray(block.jsonData)) {
        continue;
      }

      const found = block.jsonData.find((item) =>
        this.isInstitutionalRatingData(item, isKnownRatingSection),
      );

      if (found) {
        return found;
      }
    }

    return null;
  }

  private isInstitutionalRatingData(
    item: SectionJsonData,
    isKnownRatingSection: boolean,
  ): boolean {
    const rows = Array.isArray(item?.rows) ? item.rows : [];

    const hasRatingRows = rows.some(
      (row) =>
        row &&
        Boolean(
          row.category ||
            row.weight ||
            row.currentScore ||
            row.currentRating ||
            row.previousScore ||
            row.previousRating,
        ),
    );

    const finalScore = item?.finalScore;

    const hasRatingFinalScore = Boolean(
      finalScore &&
        (finalScore.currentScore ||
          finalScore.currentRating ||
          finalScore.previousScore ||
          finalScore.previousRating),
    );

    if (hasRatingRows || hasRatingFinalScore) {
      return true;
    }

    // A generic object only carrying introduction/conclusion/rows (with no
    // rating-specific fields) is not institutional rating data unless the
    // section itself is a known institutional-rating section.
    return (
      isKnownRatingSection &&
      (Boolean(item?.introduction) || Boolean(item?.conclusion) || rows.length > 0)
    );
  }

  private buildInstitutionalRating(data: SectionJsonData): Array<Paragraph | Table> {
    const output: Array<Paragraph | Table> = [];

    if (data.introduction) {
      output.push(...this.bodyParagraphs(data.introduction));
    }

    const rows: TableRow[] = [
      new TableRow({
        tableHeader: true,
        children: [
          this.headerCell('Category', { rowSpan: 2 }),
          this.headerCell('Weight', { rowSpan: 2 }),
          this.headerCell('Current Rating', { columnSpan: 2 }),
          this.headerCell('Previous Rating', { columnSpan: 2 }),
        ],
      }),
      new TableRow({
        tableHeader: true,
        children: [
          this.headerCell('Score'),
          this.headerCell('Rating'),
          this.headerCell('Score'),
          this.headerCell('Rating'),
        ],
      }),
    ];

    (data.rows ?? []).forEach((row) => {
      rows.push(
        new TableRow({
          cantSplit: true,
          children: [
            this.bodyCell(row.category, { bold: true }),
            this.bodyCell(row.weight, { center: true }),
            this.bodyCell(row.currentScore, { center: true }),
            this.bodyCell(row.currentRating, { center: true }),
            this.bodyCell(row.previousScore, { center: true }),
            this.bodyCell(row.previousRating, { center: true }),
          ],
        }),
      );
    });

    if (data.finalScore) {
      rows.push(
        new TableRow({
          cantSplit: true,
          children: [
            this.bodyCell(data.finalScore.title, { bold: true }),
            this.bodyCell(''),
            this.bodyCell(data.finalScore.currentScore, { center: true, bold: true }),
            this.bodyCell(data.finalScore.currentRating, { center: true, bold: true }),
            this.bodyCell(data.finalScore.previousScore, { center: true, bold: true }),
            this.bodyCell(data.finalScore.previousRating, { center: true, bold: true }),
          ],
        }),
      );
    }

    output.push(
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows,
        borders: this.tableBorders(),
      }),
    );

    if (data.conclusion) {
      output.push(...this.bodyParagraphs(data.conclusion));
    }

    return output;
  }

  /* ====================================================== */
  /* RECOMMENDATION TABLE                                  */
  /* ====================================================== */

  private getRecommendationRows(section: ReportSection): SectionJsonData[] {
    return section.blocks
      .flatMap((block) => (Array.isArray(block.jsonData) ? block.jsonData : []))
      .filter((item) => Array.isArray(item.recommendationBlocks));
  }

  private buildRecommendationTable(items: SectionJsonData[]): Table {
    const rows: TableRow[] = [
      new TableRow({
        tableHeader: true,
        children: [
          this.headerCell('Sr.', { widthPct: 6 }),
          this.headerCell('Description', { widthPct: 24 }),
          this.headerCell('Recommendation', { widthPct: 70 }),
        ],
      }),
    ];

    items.forEach((item, index) => {
      const recommendationParagraphs: Array<Paragraph | Table> = [];

      (item.recommendationBlocks ?? []).forEach((recommendation) => {
        if (recommendation.type === 'bullet-list') {
          recommendationParagraphs.push(
            ...this.bulletList(
              (recommendation.items ?? [])
                .map((value) => String(value ?? '').trim())
                .filter(Boolean),
              true,
            ),
          );
        } else if (recommendation.type === 'numbered-list') {
          // Numbered within the row (1.1, 1.2…) to match the report, rather
          // than restarting at "1." under the row's own number.
          const lines = (recommendation.items ?? [])
            .map((value) => this.stripNumber(value))
            .filter(Boolean);

          recommendationParagraphs.push(
            ...lines.map((line, lineIndex) =>
              this.scopedNumberedParagraph(
                scopedListLabel(
                  item.serialNo ?? index + 1,
                  item.recommendationBlocks ?? [],
                  recommendation,
                  lineIndex,
                  (block) => block?.type === 'numbered-list',
                  (block) =>
                    (block?.items ?? []).map((value) => this.stripNumber(value)).filter(Boolean)
                      .length,
                ),
                line,
              ),
            ),
          );
        } else if (recommendation.type === 'paragraph' && recommendation.content) {
          recommendationParagraphs.push(
            ...this.bodyParagraphs(recommendation.content, true),
          );
        }
      });

      rows.push(
        new TableRow({
          cantSplit: recommendationParagraphs.length <= 4,
          children: [
            this.bodyCell(String(item.serialNo ?? index + 1), { center: true }),
            this.bodyCell(item.description ?? '', { bold: true }),
            new TableCell({
              verticalAlign: VerticalAlign.TOP,
              margins: this.cellMargins(),
              children:
                recommendationParagraphs.length > 0
                  ? recommendationParagraphs
                  : [new Paragraph('')],
            }),
          ],
        }),
      );
    });

    return new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows,
      borders: this.tableBorders(),
    });
  }

  /* ====================================================== */
  /* REGULAR BLOCKS                                        */
  /* ====================================================== */

  private buildRegularBlocks(
    blocks: CreditSectionBlock[],
  ): Array<Paragraph | Table> {
    const output: Array<Paragraph | Table> = [];

    blocks.forEach((block) => {
      if (Number(block.blkTyp) === 3) {
        if (Array.isArray(block.jsonData) && block.jsonData.length > 0) {
          block.jsonData.forEach((item) => {
            const title = String(item.title ?? '').trim();
            const content = this.getHighlightItemContent(item);

            if (isRichTextHtml(content)) {
              // Rich text spans several paragraphs, so the title gets its own line.
              if (title) {
                output.push(this.labelledParagraph(title, ''));
              }

              output.push(...this.richTextParagraphs(content, false));
            } else if (title || content) {
              output.push(this.labelledParagraph(title, content));
            }
          });
        } else {
          const content = this.blockContent(block);
          if (content) {
            output.push(...this.bodyParagraphs(content));
          }
        }

        return;
      }

      if (Number(block.blkTyp) === 2) {
        const bullets = this.getBullets(block);

        if (bullets.length > 0) {
          output.push(...this.bulletList(bullets));
        } else {
          const content = this.blockContent(block);
          if (content) {
            output.push(...this.bodyParagraphs(content));
          }
        }

        return;
      }

      const content = this.blockContent(block);
      if (content) {
        output.push(...this.bodyParagraphs(content));
      }
    });

    return output;
  }

  private getBullets(block: CreditSectionBlock): string[] {
    const bullets: string[] = [];

    (block.jsonData ?? []).forEach((data) => {
      if (Array.isArray(data.bullets)) {
        data.bullets.forEach((item) => {
          const value = String(item ?? '').trim();
          if (value) {
            bullets.push(value);
          }
        });
      }

      if (data.showBullet && data.content) {
        this.listLines(data.content).forEach((line) => bullets.push(line));
      }
    });

    return bullets;
  }

  private blockContent(block: CreditSectionBlock): string {
    const jsonContent = (block.jsonData ?? [])
      .filter((item) => !item.showBullet && !Array.isArray(item.blocks))
      .map((item) => this.getHighlightItemContent(item))
      .filter(Boolean)
      .join('\n\n');

    return jsonContent || String(block.txtData ?? '').trim();
  }

  private getHighlightItemContent(item: SectionJsonData): string {
    const content = String(item?.content ?? '').trim();

    if (content) {
      return content;
    }

    if (Array.isArray(item?.paragraphs)) {
      return joinRichText(item.paragraphs);
    }

    return '';
  }

  /* ====================================================== */
  /* PARAGRAPH BUILDERS                                    */
  /* ====================================================== */

  private sectionTitle(
    text: string,
    sectionNumber: number,
    /** "f)" / "1." from sectionHeadingLabel; defaults to "<sectionNumber>.". */
    label: string = `${sectionNumber}.`,
  ): Paragraph {
    // "2.1"-style labels are sub-sections: indented and a size smaller, as
    // the reference prints "2.1 Financial Performance:".
    const isSubSection = /^\d+\.\d+$/.test(label);

    return new Paragraph({
      keepNext: true,
      keepLines: true,
      contextualSpacing: false,
      alignment: AlignmentType.LEFT,
      indent: isSubSection ? { left: 360 } : undefined,
      spacing: {
        before: sectionNumber === 1 ? 0 : MAIN_HEADING_BEFORE,
        after: MAIN_HEADING_AFTER,
        line: MAIN_HEADING_LINE,
        lineRule: LineRuleType.AUTO,
      },
      children: [
        new TextRun({
          text: `${label} ${String(text ?? '').trim()}:`,
          bold: true,
          font: FONT,
          size: isSubSection ? SUB_HEADING_SIZE : MAIN_HEADING_SIZE,
          color: BLACK,
        }),
      ],
    });
  }

  private subHeading(text: string): Paragraph {
    return new Paragraph({
      keepNext: true,
      keepLines: true,
      contextualSpacing: false,
      alignment: AlignmentType.LEFT,
      spacing: {
        before: SUB_HEADING_BEFORE,
        after: SUB_HEADING_AFTER,
        line: MAIN_HEADING_LINE,
        lineRule: LineRuleType.AUTO,
      },
      children: [
        new TextRun({
          text,
          bold: true,
          font: FONT,
          size: SUB_HEADING_SIZE,
          color: BLACK,
        }),
      ],
    });
  }

  private smallHeading(text: string): Paragraph {
    return new Paragraph({
      keepNext: true,
      keepLines: true,
      contextualSpacing: false,
      alignment: AlignmentType.LEFT,
      spacing: {
        before: SMALL_HEADING_BEFORE,
        after: SMALL_HEADING_AFTER,
        line: MAIN_HEADING_LINE,
        lineRule: LineRuleType.AUTO,
      },
      children: [
        new TextRun({
          text,
          bold: true,
          font: FONT,
          size: SMALL_HEADING_SIZE,
          color: BLACK,
        }),
      ],
    });
  }

  private compactBodyParagraph(text: string): Paragraph {
    return new Paragraph({
      alignment: AlignmentType.JUSTIFIED,
      contextualSpacing: false,
      spacing: {
        before: 0,
        after: BODY_SPACING_AFTER,
        line: BODY_LINE_SPACING,
        lineRule: LineRuleType.AUTO,
      },
      children: [
        new TextRun({
          text,
          font: FONT,
          size: BODY_TEXT_SIZE,
          color: BLACK,
        }),
      ],
    });
  }

  /**
   * Resolves the alignment/size/spacing for normal report text.
   * - compact=true: dense table-cell paragraphs (recommendation cells).
   * - executiveSummaryContext: the narrow first-page Executive Summary
   *   column.
   * - otherwise: full-width main report body.
   * All three are JUSTIFIED (document content is justified throughout) and
   * always set an explicit AUTO lineRule with contextualSpacing disabled.
   */
  private resolveBodyRunStyle(compact: boolean) {
    if (compact) {
      return {
        alignment: AlignmentType.JUSTIFIED,
        size: TABLE_TEXT_SIZE,
        spacing: {
          before: 0,
          after: TABLE_SPACING_AFTER,
          line: TABLE_LINE_SPACING,
          lineRule: LineRuleType.AUTO,
        },
      };
    }

    if (this.executiveSummaryContext) {
      return {
        alignment: AlignmentType.JUSTIFIED,
        size: EXEC_BODY_TEXT_SIZE,
        spacing: {
          before: 0,
          after: EXEC_BODY_SPACING_AFTER,
          line: EXEC_BODY_LINE_SPACING,
          lineRule: LineRuleType.AUTO,
        },
      };
    }

    return {
      alignment: AlignmentType.JUSTIFIED,
      size: BODY_TEXT_SIZE,
      spacing: {
        before: 0,
        after: BODY_SPACING_AFTER,
        line: BODY_LINE_SPACING,
        lineRule: LineRuleType.AUTO,
      },
    };
  }

  private bodyParagraphs(content: string, compact = false): Array<Paragraph | Table> {
    if (isRichTextHtml(content)) {
      return this.richTextParagraphs(content, compact);
    }

    const style = this.resolveBodyRunStyle(compact);

    return this.paragraphChunks(content).map(
      (paragraph) =>
        new Paragraph({
          alignment: style.alignment,
          contextualSpacing: false,
          spacing: style.spacing,
          children: [
            new TextRun({
              text: paragraph,
              font: FONT,
              color: BLACK,
              size: style.size,
            }),
          ],
        }),
    );
  }

  /* ---------- Rich text (HTML from the section RichTextEditor) ---------- */

  /**
   * Converts sanitized editor HTML to Word paragraphs: <p> → paragraph,
   * <ul>/<ol> → bulleted / numbered paragraphs (each <ol> its own numbering
   * instance, like numberedList()), <table> → bordered Word table, inline
   * marks → TextRun formatting.
   */
  private richTextParagraphs(html: string, compact: boolean): Array<Paragraph | Table> {
    const style = this.resolveBodyRunStyle(compact);
    const container = document.createElement('div');
    container.innerHTML = sanitizeRichText(html);

    const output: Array<Paragraph | Table> = [];

    const paragraphFrom = (children: TextRun[]) =>
      new Paragraph({
        alignment: style.alignment,
        contextualSpacing: false,
        spacing: style.spacing,
        children,
      });

    container.childNodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) {
        const text = node.textContent?.trim();
        if (text) {
          output.push(paragraphFrom(this.richTextRuns(node, style.size, {})));
        }
        return;
      }

      const tag = node.tagName.toLowerCase();

      if (tag === 'ul' || tag === 'ol') {
        const reference = tag === 'ol' ? this.nextNumberingReference() : null;

        Array.from(node.children).forEach((item) => {
          output.push(
            new Paragraph({
              ...(reference
                ? { numbering: { reference, level: 0 } }
                : {
                    bullet: { level: 0 },
                    indent: { left: compact ? 220 : 320, hanging: compact ? 100 : 140 },
                  }),
              alignment: style.alignment,
              contextualSpacing: false,
              spacing: style.spacing,
              children: this.richTextRuns(item, style.size, {}),
            }),
          );
        });
        return;
      }

      if (tag === 'table') {
        const table = this.richTextTable(node);
        if (table) {
          output.push(table);
        }
        return;
      }

      const runs = this.richTextRuns(node, style.size, {});

      // An empty <p> is a blank line the user added with Enter, so it is kept.
      if (runs.length > 0 || tag === 'p') {
        output.push(paragraphFrom(runs.length > 0 ? runs : [new TextRun({ text: '', size: style.size })]));
      }
    });

    return output;
  }

  /** Editor <table> (e.g. pasted from Word / Excel) as a bordered Word table. */
  private richTextTable(table: HTMLElement): Table | null {
    const rows = Array.from(table.querySelectorAll('tr'))
      .map((row) => {
        const cells = Array.from(row.children).filter(
          (cell): cell is HTMLTableCellElement => cell instanceof HTMLTableCellElement,
        );

        if (!cells.length) {
          return null;
        }

        return new TableRow({
          cantSplit: true,
          tableHeader: cells.every((cell) => cell.tagName.toLowerCase() === 'th'),
          children: cells.map((cell) => {
            const isHeader = cell.tagName.toLowerCase() === 'th';
            const runs = this.richTextRuns(cell, TABLE_TEXT_SIZE, isHeader ? { bold: true } : {});

            return new TableCell({
              columnSpan: cell.colSpan > 1 ? cell.colSpan : undefined,
              rowSpan: cell.rowSpan > 1 ? cell.rowSpan : undefined,
              verticalAlign: VerticalAlign.TOP,
              margins: this.cellMargins(),
              children: [
                new Paragraph({
                  contextualSpacing: false,
                  spacing: {
                    before: 0,
                    after: 0,
                    line: TABLE_LINE_SPACING,
                    lineRule: LineRuleType.AUTO,
                  },
                  children: runs,
                }),
              ],
            });
          }),
        });
      })
      .filter((row): row is TableRow => row !== null);

    if (!rows.length) {
      return null;
    }

    return new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows,
      borders: this.tableBorders(),
    });
  }

  private richTextRuns(node: Node, baseSize: number, marks: RichTextMarks): TextRun[] {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? '';

      if (!text) {
        return [];
      }

      return [
        new TextRun({
          text,
          font: marks.font ?? FONT,
          color: marks.color ?? BLACK,
          size: marks.sizePx ? Math.round(baseSize * (marks.sizePx / RICH_TEXT_BASE_PX)) : baseSize,
          bold: marks.bold,
          italics: marks.italics,
          underline: marks.underline ? {} : undefined,
          strike: marks.strike,
          shading: marks.highlight
            ? { type: ShadingType.CLEAR, color: 'auto', fill: marks.highlight }
            : undefined,
        }),
      ];
    }

    if (!(node instanceof HTMLElement)) {
      return [];
    }

    const tag = node.tagName.toLowerCase();

    if (tag === 'br') {
      return [new TextRun({ text: '', break: 1 })];
    }

    const next: RichTextMarks = { ...marks };

    if (tag === 'strong' || tag === 'b') next.bold = true;
    if (tag === 'em' || tag === 'i') next.italics = true;
    if (tag === 'u') next.underline = true;
    if (tag === 's') next.strike = true;

    const color = cssColorToHex(node.style.color);
    if (color) next.color = color;

    const background = cssColorToHex(node.style.backgroundColor || node.getAttribute('data-color') || '');
    if (background) next.highlight = background;

    const fontFamily = node.style.fontFamily?.split(',')[0]?.replace(/["']/g, '').trim();
    if (fontFamily) next.font = fontFamily;

    const sizePx = parseFloat(node.style.fontSize);
    if (Number.isFinite(sizePx) && sizePx > 0) next.sizePx = sizePx;

    const runs: TextRun[] = [];

    node.childNodes.forEach((child, index) => {
      // A list item's content is wrapped in one or more <p>; keep them on
      // separate lines inside the same Word paragraph.
      if (index > 0 && child instanceof HTMLElement && child.tagName.toLowerCase() === 'p') {
        runs.push(new TextRun({ text: '', break: 1 }));
      }

      runs.push(...this.richTextRuns(child, baseSize, next));
    });

    return runs;
  }

  private captionParagraphs(content: string): Paragraph[] {
    return this.paragraphChunks(content).map(
      (paragraph) =>
        new Paragraph({
          alignment: AlignmentType.JUSTIFIED,
          contextualSpacing: false,
          spacing: {
            before: SMALL_HEADING_AFTER,
            after: SUB_HEADING_AFTER,
            line: TABLE_LINE_SPACING,
            lineRule: LineRuleType.AUTO,
          },
          children: [
            new TextRun({
              text: paragraph,
              font: FONT,
              color: BLACK,
              size: TABLE_TEXT_SIZE,
            }),
          ],
        }),
    );
  }

  private bulletList(items: string[], compact = false): Paragraph[] {
    const style = this.resolveBodyRunStyle(compact);

    return items.map(
      (item) =>
        new Paragraph({
          bullet: { level: 0 },
          alignment: style.alignment,
          contextualSpacing: false,
          spacing: style.spacing,
          indent: {
            left: compact ? 220 : 320,
            hanging: compact ? 100 : 140,
          },
          children: [
            new TextRun({
              text: item,
              font: FONT,
              color: BLACK,
              size: style.size,
            }),
          ],
        }),
    );
  }

  private numberedList(items: string[], compact = false): Paragraph[] {
    if (items.length === 0) {
      return [];
    }

    // Every independent numbered-list block gets its own numbering
    // instance so it restarts at 1 instead of continuing the count of
    // every other numbered list in the document.
    const reference = this.nextNumberingReference();
    const style = this.resolveBodyRunStyle(compact);

    return items.map(
      (item) =>
        new Paragraph({
          numbering: {
            reference,
            level: 0,
          },
          alignment: style.alignment,
          contextualSpacing: false,
          spacing: style.spacing,
          children: [
            new TextRun({
              text: this.stripNumber(item),
              font: FONT,
              color: BLACK,
              size: style.size,
            }),
          ],
        }),
    );
  }

  /** Compact list paragraph with an explicit label ("1.2") and a hanging indent. */
  private scopedNumberedParagraph(label: string, text: string): Paragraph {
    const style = this.resolveBodyRunStyle(true);

    return new Paragraph({
      alignment: style.alignment,
      contextualSpacing: false,
      spacing: style.spacing,
      indent: { left: 420, hanging: 420 },
      children: [
        new TextRun({ text: `${label}\t`, bold: true, font: FONT, color: BLACK, size: style.size }),
        new TextRun({ text, font: FONT, color: BLACK, size: style.size }),
      ],
      tabStops: [{ type: TabStopType.LEFT, position: 420 }],
    });
  }

  private labelledParagraph(title: string, content: string): Paragraph {
    const style = this.resolveBodyRunStyle(false);
    const children: TextRun[] = [];

    if (title) {
      children.push(
        new TextRun({
          text: `${title}${content ? ': ' : ''}`,
          bold: true,
          font: FONT,
          size: style.size,
          color: BLACK,
        }),
      );
    }

    if (content) {
      children.push(
        new TextRun({
          text: content,
          font: FONT,
          size: style.size,
          color: BLACK,
        }),
      );
    }

    return new Paragraph({
      alignment: style.alignment,
      contextualSpacing: false,
      spacing: style.spacing,
      children,
    });
  }

  /* ====================================================== */
  /* TABLE CELLS                                           */
  /* ====================================================== */

  private headerCell(
    text: string,
    options: {
      columnSpan?: number;
      rowSpan?: number;
      widthPct?: number;
      /** Fixed width in twips (for FIXED-layout tables); wins over widthPct. */
      widthTwips?: number;
      size?: number;
    } = {},
  ): TableCell {
    return new TableCell({
      columnSpan: options.columnSpan,
      rowSpan: options.rowSpan,
      width: options.widthTwips
        ? { size: options.widthTwips, type: WidthType.DXA }
        : options.widthPct
          ? { size: options.widthPct, type: WidthType.PERCENTAGE }
          : undefined,
      verticalAlign: VerticalAlign.CENTER,
      margins: this.cellMargins(),
      children: [
        new Paragraph({
          alignment: AlignmentType.CENTER,
          contextualSpacing: false,
          spacing: {
            before: 0,
            after: TABLE_SPACING_AFTER,
            line: TABLE_HEADER_LINE_SPACING,
            lineRule: LineRuleType.AUTO,
          },
          children: [
            new TextRun({
              text: String(text ?? '').trim(),
              bold: true,
              font: FONT,
              color: BLACK,
              size: options.size ?? TABLE_HEADER_TEXT_SIZE,
            }),
          ],
        }),
      ],
    });
  }

  private bodyCell(
    text: string,
    options: {
      bold?: boolean;
      center?: boolean;
      /** Amounts: right-aligned, vertically centred like other value cells. */
      right?: boolean;
      rowSpan?: number;
      columnSpan?: number;
      size?: number;
      /** Fixed width in twips (for FIXED-layout tables). */
      widthTwips?: number;
    } = {},
  ): TableCell {
    return new TableCell({
      rowSpan: options.rowSpan,
      columnSpan: options.columnSpan,
      width: options.widthTwips
        ? { size: options.widthTwips, type: WidthType.DXA }
        : undefined,
      // Numeric/short-value cells are vertically centered; descriptive
      // (left-aligned) text cells align to the top of the row instead.
      verticalAlign: options.center || options.right ? VerticalAlign.CENTER : VerticalAlign.TOP,
      margins: this.cellMargins(),
      children: [
        new Paragraph({
          alignment: options.right
            ? AlignmentType.RIGHT
            : options.center
              ? AlignmentType.CENTER
              : AlignmentType.LEFT,
          contextualSpacing: false,
          spacing: {
            before: 0,
            after: TABLE_SPACING_AFTER,
            line: TABLE_LINE_SPACING,
            lineRule: LineRuleType.AUTO,
          },
          children: [
            new TextRun({
              text: String(text ?? ''),
              bold: Boolean(options.bold),
              font: FONT,
              color: BLACK,
              size: options.size ?? TABLE_TEXT_SIZE,
            }),
          ],
        }),
      ],
    });
  }

  private tableBorders() {
    return {
      top: THIN_BORDER,
      bottom: THIN_BORDER,
      left: THIN_BORDER,
      right: THIN_BORDER,
      insideHorizontal: THIN_BORDER,
      insideVertical: THIN_BORDER,
    };
  }

  private cellMargins() {
    return {
      top: 85,
      bottom: 85,
      left: 100,
      right: 100,
    };
  }

  /* ====================================================== */
  /* GENERAL HELPERS                                       */
  /* ====================================================== */

  private paragraphChunks(content: string): string[] {
    return String(content ?? '')
      .replace(/\r\n/g, '\n')
      .split(/\n\s*\n/)
      .map((paragraph) => paragraph.replace(/\n+/g, ' ').trim())
      .filter(Boolean);
  }

  private listLines(content: string | undefined): string[] {
    return String(content ?? '')
      .replace(/\r\n/g, '\n')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
  }

  private stripNumber(value: string): string {
    return String(value ?? '')
      .replace(/^\s*\d+[.)]\s*/, '')
      .trim();
  }

  /**
   * Heading label of `sections[index]` after the first page, as in the
   * reference: sections before Introduction carry on the Executive
   * Summary's letters (a)–e) on the first page, then "f) Borrower Request for
   * funding", "g) Summary Terms and Conditions"); Introduction onwards are
   * numbered from 1. Without a first page (letterOffset 0) or an
   * Introduction, every section is simply numbered.
   */
  private sectionHeadingLabel(
    sections: ReportSection[],
    index: number,
    letterOffset: number,
  ): string {
    const introductionIndex = sections.findIndex(
      (section) => Number(section.refValSeq) === this.INTRODUCTION_REF_VAL_SEQ,
    );
    const lettered = letterOffset > 0 && introductionIndex !== -1;

    if (lettered && index < introductionIndex) {
      return `${this.alphabeticPrefix(letterOffset + index)})`;
    }

    // Numbered part: a SUB_SECTION_REF_VAL_SEQS section is numbered under the
    // section before it ("2.1 Financial Performance" under "2. Score on
    // Institutional Rating System"), as in the reference; the rest count on.
    let main = 0;
    let sub = 0;

    for (let position = lettered ? introductionIndex : 0; position <= index; position++) {
      const isSub =
        main > 0 &&
        position < sections.length &&
        this.SUB_SECTION_REF_VAL_SEQS.includes(Number(sections[position].refValSeq));

      if (isSub) {
        sub++;
      } else {
        main++;
        sub = 0;
      }
    }

    return sub > 0 ? `${main}.${sub}` : `${main}.`;
  }

  private alphabeticPrefix(index: number): string {
    const normalizedIndex = Math.max(0, index);
    return String.fromCharCode(97 + (normalizedIndex % 26));
  }

  private ensureCellChildren(
    children: Array<Paragraph | Table>,
  ): Array<Paragraph | Table> {
    return children.length > 0 ? children : [new Paragraph('')];
  }

  private withExecutiveSummaryContext<T>(factory: () => T): T {
    const previousValue = this.executiveSummaryContext;
    this.executiveSummaryContext = true;

    try {
      return factory();
    } finally {
      this.executiveSummaryContext = previousValue;
    }
  }

  private safeFileName(fileName: string): string {
    return String(fileName ?? 'Credit-Proposal-Report')
      .trim()
      .replace(/[\\/:*?"<>|]+/g, '-')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '') || 'Credit-Proposal-Report';
  }

  private baseStyles(): IStylesOptions {
    return {
      default: {
        document: {
          run: {
            font: FONT,
            size: BODY_TEXT_SIZE,
            color: BLACK,
          },
          paragraph: {
            contextualSpacing: false,
            spacing: {
              before: 0,
              after: BODY_SPACING_AFTER,
              line: BODY_LINE_SPACING,
              lineRule: LineRuleType.AUTO,
            },
          },
        },
      },
    };
  }
}